/**
 * Translation dictionaries for every surface the unified app renders.
 *
 * Keys are organized by surface:
 *   common.*   reused terms (Save, Cancel, Loading, Yes, No, Back)
 *   nav.*      AppShell top nav
 *   footer.*   AppShell footer
 *   auth.*     sign-in / sign-up modal
 *   settings.* /unified/settings screen
 *   chat.*     /unified/chat UI chrome (AI responses are language-switched server-side)
 *   discover.* /unified/discover filter bar + banner
 *   dashboard.* /unified/dashboard tabs + heroes
 *   restaurant.* /unified/restaurant/:id page
 *   menu_manager.* owner-side menu editor
 *   safety.*   /unified/safety-signals
 *   landing.*  /unified hero
 *   toasts.*   transient status messages
 *
 * Strings not yet translated fall back to English automatically, so adding
 * a new key anywhere is safe. Languages below cover ~95% of diners by
 * volume — add more by following the same key set.
 */

export const SUPPORTED_LANGUAGES = ['en', 'es', 'fr', 'pt', 'it', 'de', 'ja', 'zh'];

const en = {
  common: {
    save: 'Save', cancel: 'Cancel', close: 'Close', back: 'Back', continue: 'Continue',
    yes: 'Yes', no: 'No', loading: 'Loading…', saving: 'Saving…', saved: 'Saved.',
    edit: 'Edit', delete: 'Delete', add: 'Add', remove: 'Remove', done: 'Done',
    search: 'Search', filter: 'Filter', clear: 'Clear', apply: 'Apply',
    next: 'Next', previous: 'Previous', skip: 'Skip', submit: 'Submit',
    confirm: 'Confirm', required: 'Required', optional: 'Optional',
    error: 'Something went wrong.', retry: 'Try again',
  },
  nav: {
    discover: 'Discover',
    for_restaurants: 'For Business',
    pricing: 'Pricing',
    dashboard: 'Dashboard',
    notifications: 'Notifications',
    sign_in: 'Sign in',
    sign_out: 'Sign out',
    get_started: 'Get Started',
    home_aria: 'Home',
  },
  footer: {
    how_it_works: 'How it works',
    about: 'About',
    billing: 'Billing',
    settings: 'Settings',
    terms: 'Terms',
    privacy: 'Privacy',
    contact: 'Contact',
    copyright: '© {year} Chat With Menu',
  },
  auth: {
    sign_in_title: 'Welcome back',
    sign_up_title: 'Create your account',
    sign_in_sub: 'Sign in to pick up where you left off.',
    sign_up_sub: 'Save restaurants, chat with menus, earn points.',
    tab_sign_in: 'Sign in',
    tab_sign_up: 'Create account',
    full_name: 'Full name',
    name_placeholder: 'Your name',
    email: 'Email',
    email_placeholder: 'you@example.com',
    password: 'Password',
    password_placeholder: 'At least 8 characters',
    show: 'Show',
    hide: 'Hide',
    show_password: 'Show password',
    hide_password: 'Hide password',
    // `continue_with_google`, `continue_with_apple`, `or_email`, and
    // `err_oauth_soon` are retained so older builds / other surfaces
    // that still reference them don't crash. They're no longer rendered
    // by AuthModal (social sign-in buttons removed).
    continue_with_google: 'Continue with Google',
    continue_with_apple: 'Continue with Apple',
    or_email: 'or continue with email',
    err_oauth_soon: 'Google and Apple sign-in are almost ready. Please continue with email for now.',
    no_account: "Don't have an account?",
    have_account: 'Already have an account?',
    forgot: 'Forgot password?',
    submit_sign_in: 'Sign in',
    submit_sign_up: 'Create account',
    submitting_sign_in: 'Signing in…',
    submitting_sign_up: 'Creating account…',
    err_invalid: 'Invalid credentials. Please try again.',
    err_network: 'Could not reach server. Check your connection and try again.',
    // Agreement gate (signup only). Rendered around two <Link>s so the
    // prefix/connector/suffix have to stay short and not assume a
    // particular word order — keep them punctuation-adjacent.
    agree_prefix: 'I agree to the',
    agree_terms_link: 'Terms of Service',
    agree_and: 'and',
    agree_privacy_link: 'Privacy Policy',
    err_must_agree: 'Please agree to the Terms of Service and Privacy Policy to continue.',
  },
  settings: {
    eyebrow: 'SETTINGS',
    title_l1: 'Tune how we',
    title_l2: 'reach you',
    subtitle: 'Notifications, privacy, language. For dietary profile, use Dashboard → Allergies.',
    channels_eyebrow: 'HOW WE REACH YOU',
    channels_title: 'Notification channels',
    push: 'Push notifications',
    push_hint: 'On this device and the mobile app.',
    email: 'Email',
    email_hint: 'Billing receipts always come through regardless.',
    sms: 'SMS',
    sms_hint: 'Time-sensitive alerts only. Standard rates apply.',
    events_eyebrow: 'EVENT TYPES',
    events_title: 'What to notify me about',
    cat_table_invites: 'Table invites',
    cat_table_invites_hint: 'Someone asks to join your table, or accepts your invite',
    cat_safety_alerts: 'Safety alerts',
    cat_safety_alerts_hint: 'Reactions, recalls, or new allergen risks at spots you follow',
    cat_dish_matches: 'Dish matches',
    cat_dish_matches_hint: 'New safe dishes that match your dietary profile',
    cat_order_updates: 'Order updates',
    cat_order_updates_hint: 'Accepted, ready for pickup, delivered',
    cat_reviews_on_me: 'Replies to your reviews',
    cat_reviews_on_me_hint: 'Restaurants reply to something you posted',
    cat_billing: 'Billing',
    cat_billing_hint: 'Invoices, plan changes, payment failures',
    cat_product_updates: 'Product updates',
    cat_product_updates_hint: 'New features we ship (low volume)',
    cat_weekly_digest: 'Weekly digest',
    cat_weekly_digest_hint: 'Sunday summary of new spots + notes from your table',
    quiet_eyebrow: "DON'T PING ME",
    quiet_title: 'Quiet hours',
    quiet_toggle: 'Silence non-critical pings overnight',
    quiet_hint: 'Safety alerts still come through.',
    quiet_from: 'FROM',
    quiet_to: 'TO',
    privacy_eyebrow: 'WHAT OTHERS SEE',
    privacy_title: 'Privacy',
    priv_share_prefs: 'Share my dietary profile with restaurants when I chat',
    priv_share_prefs_hint: "Lets servers pre-see your restrictions so you don't have to repeat them every time.",
    priv_attribute_signals: 'Attribute my safety signals publicly',
    priv_attribute_signals_hint: 'Off = your signals are anonymous.',
    priv_show_review_name: 'Show my name on reviews',
    priv_show_review_name_hint: "Off = posted as 'Anonymous diner'.",
    priv_allow_invites: 'Allow table invites from friends',
    priv_allow_invites_hint: 'Off = only invites you start yourself.',
    locale_eyebrow: 'LOCALE',
    locale_title: 'Language & currency',
    language: 'Language',
    currency: 'Currency',
    measurements: 'Measurements',
    imperial: 'Imperial (fl oz, °F)',
    metric: 'Metric (ml, °C)',
    danger_eyebrow: 'DANGER ZONE',
    danger_title: 'Account actions',
    reset: 'Reset settings to defaults',
    reset_hint: "Notifications, privacy, locale. Won't touch your dietary profile or saved restaurants.",
    reset_action: 'Reset',
    download: 'Download my data',
    download_hint: 'Preferences, reviews, saved spots — in JSON. Email will arrive within 24 hours.',
    download_action: 'Request',
    delete_account: 'Delete account',
    delete_account_hint: "Removes your profile, reviews, and signals. Can't be undone.",
    delete_action: 'Delete',
  },
  chat: {
    empty_title: 'Chat with the menu',
    empty_subtitle: 'Ask about allergens, pairings, or what the kitchen can customize — you can be specific.',
    gate_no_restaurant_title: 'Pick a restaurant first',
    gate_no_restaurant_sub: "Open chat from a restaurant card so we know which menu to help with.",
    gate_no_restaurant_cta: 'Browse restaurants',
    gate_signin_title: 'Sign in to chat',
    gate_signin_sub: 'Save your conversation, pick dishes, and unlock safety insights.',
    gate_signin_cta: 'Sign in',
    input_placeholder: 'Ask about an allergen, a dish, or the vibe…',
    send: 'Send',
    send_failed: 'Message failed. Try again.',
    ai_offline: 'The assistant is catching its breath. Try again in a moment.',
    reset_chat: 'New conversation',
    suggested_prompts_label: 'Try asking',
    suggest_allergens: 'What on this menu is gluten-free and dairy-free?',
    suggest_recommend: "What's the kitchen most proud of tonight?",
    suggest_pair: 'What pairs well with the salmon?',
    suggest_cross: 'Do you have a peanut-free station?',
  },
  discover: {
    hero_title: "Restaurants your body will thank you for",
    hero_subtitle: "Swipe right to save, left to skip — we'll learn what works for you.",
    brand_tagline: 'Restaurant Discovery',
    auto_banner_title: 'Filtered from your profile',
    auto_banner_body: ' — showing spots that match {tags}.',
    clear_auto: 'Show everything',
    result_count: '{shown} of {total} spots',
    empty: 'No spots match these filters. Try clearing a tag.',
  },
  dashboard: {
    welcome: 'Welcome, {name}',
    welcome_fallback: 'Welcome…',
    subtitle_diner:    'Move between discovery, family, and table tools without losing your place.',
    subtitle_merchant: 'Manage your restaurants, menus, and guest signals.',
    subtitle_server:   'Quick safety lookups and specials while you serve guests.',
    role_diner:   'Diner',
    role_admin:   'Admin',
    role_owner:   'Restaurant Owner',
    role_server:  'Server',
    tab_allergies:   'Allergies',
    tab_preferences: 'Preferences',
    tab_profile:     'Edit Profile',
    tab_family:      'Family',
    tab_table:       'My Table',
    tab_history:     'History',
    tab_rewards:     'Rewards',
    tab_discover:    'Discovery',
    tab_operations:  'Operations',
    tab_menus:       'Manage Menus',
    tab_add_rest:    'Add Restaurant',
    tab_service:     'Service Tools',
    // Panel header strip (eyebrow/title/subtitle above each tab body)
    panel_discovery_eyebrow: 'Discovery',
    panel_discovery_title:   'Find places that fit your table',
    panel_discovery_sub:     'Swipe, compare, and switch to map view without leaving your dashboard.',
    panel_profile_eyebrow:   'Profile',
    panel_profile_title:     'Your identity + needs',
    panel_profile_sub:       'Account basics plus dietary style, medical needs, ethical choices, and sensory preferences.',
    panel_allergies_eyebrow: 'Allergies',
    panel_allergies_title:   'Track the ingredients that matter',
    panel_allergies_sub:     'Update allergy data cleanly so chat and recommendations stay trustworthy.',
    panel_preferences_eyebrow: 'Preferences',
    panel_preferences_title: 'Teach the app what you like',
    panel_preferences_sub:   'Voice notes, food preferences, and taste signals should feel personal.',
    panel_family_eyebrow:    'Family',
    panel_family_title:      'Manage the people you protect',
    panel_family_sub:        'Shared profiles and dietary context together in one place.',
    panel_table_eyebrow:     'My Table',
    panel_table_title:       'Build your dining support network',
    panel_table_sub:         'Connections, invites, discovery, and safety together.',
    panel_rewards_eyebrow:   'Rewards',
    panel_rewards_title:     'Points, perks, and dining momentum',
    panel_rewards_sub:       'Rewards are coming soon. Nothing to redeem yet.',
  },
  dish: {
    back: 'Back',
    menu_fallback: 'MENU',
    needs_review: 'Needs review',
    ask_about_this: 'Ask about this',
    next_step_eyebrow: 'NEXT STEP',
    next_step_title: 'Not sure if this dish is safe for you?',
    next_step_body: "Chat with {name}'s menu — we'll cross-check your allergies in one tap.",
    share_safety_cta: 'Share safety signal',
    chat_about: 'Chat about {name}',
    // Allergen matrix
    allergen_eyebrow: 'ALLERGEN DETAILS',
    allergen_clear_title: 'No flagged allergens',
    allergen_clear_body: 'Always confirm with your server if you have a severe allergy.',
    allergen_count: '{count} allergens to be aware of',
    allergen_count_one: '1 allergen to be aware of',
    allergen_hint: "Severity inferred from this dish's tags, prep methods, and removable ingredients.",
    sev_contains: 'Contains',
    sev_prepared: 'Prepared with',
    sev_may_contain: 'May contain',
    sev_critical: 'Critical',
    // Prep
    prep_eyebrow: 'PREPARATION',
    prep_title: "How it's made",
    prep_empty: "No prep details yet. Ask the kitchen — they'll know.",
    prep_methods_eyebrow: 'METHODS',
    prep_removable_eyebrow: 'CAN BE REMOVED',
    prep_mods_eyebrow: 'CUSTOMIZATIONS',
    prep_required: 'required',
    // Pairings
    pair_eyebrow: 'PAIRS WELL WITH',
    pair_title: 'Build a full meal',
    pair_empty: "We'll suggest pairings once the menu has more items.",
    pair_drink: 'A drink pair',
    pair_start: 'To start',
    pair_finish: 'To finish',
    pair_side: 'On the side',
    // Similar
    similar_eyebrow_prefix: 'MORE FROM',
    similar_menu_fallback: 'THIS MENU',
    similar_title: 'Similar dishes',
    similar_no_allergens: 'No flagged allergens',
    similar_n_allergens: '{n} allergens',
    similar_one_allergen: '1 allergen',
    // States
    loading: 'Loading dish…',
    not_found_title: 'Dish not found',
    not_found_body: 'The item may have been removed from the menu, or the link is incorrect.',
    not_found_cta: 'Back',
  },
  restaurant: {
    write_review: 'Write a review',
    chat_cta: 'Chat with Menu',
    safety_signal_cta: 'Share safety signal',
    ask_table_cta: 'Ask the table',
    dietary_eyebrow: 'WHAT THEY SUPPORT',
    menu_highlights: 'MENU HIGHLIGHTS',
    contact_eyebrow: 'CONTACT',
    contact_none: 'Contact info not published.',
    safety_eyebrow: 'SAFETY SIGNALS',
    safety_body: 'Community-verified allergy-safe dishes at this restaurant. Share yours to help the table.',
    view_signals: 'View signals',
    see_full_menu: 'See full menu in chat',
    no_menu: 'No menu published yet. Chat with the restaurant for up-to-date options.',
    reviews_title: 'What diners say',
    reviews_eyebrow: 'RECENT REVIEWS',
    reviews_count: '{count} reviews',
    reviews_safety_eyebrow: 'ALLERGY SAFETY',
    reviews_safety_hint: 'How confident diners felt asking about allergens.',
    reviews_see_all: 'See all {count} reviews',
    loading: 'Loading restaurant…',
    not_found_title: 'Restaurant not found',
    not_found_body: 'It may have been removed, or the link is incorrect.',
    not_found_cta: 'Browse restaurants',
    time_today: 'today',
    time_yesterday: 'yesterday',
    time_days_ago: '{n} days ago',
    time_week_ago: '1 week ago',
    time_weeks_ago: '{n} weeks ago',
    time_month_ago: '1 month ago',
    time_months_ago: '{n} months ago',
  },
  menu_manager: {
    eyebrow: 'MENU MANAGER',
    title_l1: 'Your menu,',
    title_l2: 'clean and current',
    subtitle: 'Browse and edit items, publish specials, tune upsell tips, and keep your profile sharp.',
    tab_items: 'Menu Items',
    tab_specials: 'Specials',
    tab_upsell: 'Upsell Tips',
    tab_profile: 'Profile',
    publish: 'Publish menu',
    publish_n: 'Publish ({n})',
    publish_first: 'Publish your menu',
    discard: 'Discard draft',
    status_live: 'Live',
    status_unpublished: '{n} unpublished',
    status_not_published: 'Not published',
    saved_toast: 'Menu published — diners see this now.',
    reverted_toast: 'Reverted to the live version.',
  },
  safety: {
    eyebrow: 'SAFETY SIGNALS',
    title_l1: "Know what's",
    title_l2: 'actually safe',
    subtitle: 'Community-verified reports of allergy-safe dishes, graded by who confirmed them — server, kitchen, or lived experience.',
    share_cta: 'Share a signal',
    // Filter bar
    filter_label: 'Filter',
    filter_all: 'All',
    filter_worked: '✓ Worked',
    filter_reactions: '⚠ Reactions',
    filter_all_restaurants: 'All restaurants',
    filter_all_restrictions: 'All restrictions',
    results_count: '{shown} of {total} signals',
    // Empty + loaders
    empty_title: 'No signals yet for this filter',
    empty_body: 'Be first to share. A single confirmed dish helps dozens of diners decide.',
    load_failed: 'Failed to load safety signals.',
    // Report type toggle
    type_worked: 'What worked',
    type_worked_hint: 'Safe dish to recommend',
    type_wrong: 'Went wrong',
    type_wrong_hint: 'Report a reaction',
    // Severity
    severity: 'Severity',
    sev_mild: 'Mild',
    sev_mild_hint: 'Itch, minor discomfort',
    sev_moderate: 'Moderate',
    sev_moderate_hint: 'Hives, stomach upset',
    sev_severe: 'Severe',
    sev_severe_hint: 'Swelling, breathing, vomiting',
    sev_life: 'Life-threatening',
    sev_life_hint: 'Anaphylaxis, ER visit',
    // Dialog
    share_dialog_eyebrow: 'SHARE A SIGNAL',
    share_dialog_title_positive: 'What worked for you?',
    share_dialog_title_negative: 'Report a reaction',
    field_restaurant: 'Restaurant',
    restaurant_placeholder: 'Select a restaurant…',
    field_dish: 'Dish name',
    dish_placeholder: 'e.g. Gluten-free pappardelle',
    field_restrictions: 'Restrictions met',
    what_worked_label: 'What worked',
    what_happened_label: 'What happened',
    what_worked_placeholder: 'Kitchen prepared a dedicated gluten-free station with olive oil and fresh vegetables…',
    what_happened_placeholder: "I told the server I have a dairy allergy, but the dish came with a cream sauce. The kitchen didn't catch it.",
    add_photo: 'Add a photo (optional)',
    notes_label: 'Additional notes (optional)',
    notes_placeholder: 'Staff was very knowledgeable about cross-contamination…',
    confidence: 'Confidence',
    evidence: 'Evidence',
    anonymous: 'Share anonymously',
    cancel: 'Cancel',
    submit: 'Share signal',
    // Validation errors
    err_pick_restaurant: 'Please select a restaurant.',
    err_name_dish: 'Please name the dish.',
    err_pick_restriction: 'Pick at least one restriction.',
    err_describe_worked: 'Describe what worked.',
    err_describe_happened: 'Describe what happened.',
    err_pick_severity: 'Pick a severity level.',
    err_share_failed: 'Failed to share signal.',
    share_success: 'Safety signal shared — thank you.',
    // Photo picker
    photo_drop_hint: 'Click or drag a photo of the dish, menu, or receipt',
    photo_drop_active: 'Drop the photo here',
    photo_size_note: 'JPG, PNG, WebP, HEIC, or GIF — up to 10 MB',
    photo_replace: 'Replace',
    photo_remove: 'Remove',
    photo_not_image: 'Not an image',
    photo_too_large: 'Too large (10 MB max)',
    // Card chrome
    reaction_report: 'Reaction report',
    card_by: 'by {name}',
    card_anon: 'shared anonymously',
    // Evidence types
    evidence_menu_label: 'Menu label',
    evidence_server_confirmed: 'Server confirmed',
    evidence_kitchen_confirmed: 'Kitchen confirmed',
    evidence_user_experience: 'User experience',
    // Verification states
    ver_unverified: 'Unverified',
    ver_restaurant: 'Restaurant verified',
    ver_staff: 'Staff verified',
    ver_kitchen: 'Kitchen confirmed',
  },
  landing: {
    eyebrow: 'AI-powered discovery',
    headline_l1: 'Never stress',
    headline_l2: 'about',
    headline_emphasis: 'what to eat',
    sub: "Tell any menu your allergies, your diet, and what you're hungry for. It picks the dishes you can actually order.",
    pick_eyebrow: 'Pick what matters to you',
    pick_tag_in: 'Pick a {category} tag',
    pick_see_all: 'See all 9 categories',
    cta_primary: 'Try it free',
    cta_secondary: 'See it in action',
    preview_example: 'Example preview',
    // "verified" used to live here but it implies an audit process we
    // haven't actually performed — "recommended" is the true claim.
    // The number itself comes from listRestaurants `total` at runtime.
    stat_restaurants:   'restaurants recommended',
    // Taxonomy category labels for the landing hero CategoryPicker.
    group_religious:              'Religious',
    group_diet:                   'Diet',
    group_allergies:              'Allergies',
    group_therapeutic:            'Therapeutic',
    group_lifestyle:              'Lifestyle',
    group_ethical:                'Ethical',
    group_life_stage:             'Life Stage',
    group_glp1_portions:          'GLP-1 / Portions',
    group_sensory_neurodivergent: 'Sensory / Neurodivergent',

    // === SECTION: What safe looks like ===
    safe_eyebrow: 'THIS IS WHAT SAFE LOOKS LIKE',
    safe_title: 'Every tag,',
    safe_title_em: 'sourced.',
    safe_sub: "Every tag shows where the claim came from: the uploaded menu, the kitchen, a staff member, or another diner. We don't guess.",
    safe_disclaimer: 'A preparation tool, not a medical guarantee. For severe allergies, always confirm with the kitchen.',
    safe_badge_safe: 'Kitchen-confirmed',
    safe_badge_menu: 'Menu-labeled',
    safe_badge_community: 'Diner-reported',
    safe_badge_ai: 'AI-tagged · owner reviewed',

    // === SECTION: The swipe deck ===
    // Eyebrow leads with the payoff ("hand-picked") instead of the gesture
    // ("swipe to trust it"). The count is the live listRestaurants total;
    // deck_title_fallback is used when it can't be loaded. "Recommended"
    // replaces "pre-vetted" so we don't claim an audit we don't perform.
    deck_eyebrow: 'HAND-PICKED',
    deck_title: '{count} restaurants,',
    deck_title_fallback: 'Real restaurants,',
    deck_title_em: 'recommended.',
    deck_sub: "Every card shows what's safe to order. Independents, chains, and caterers, all recommended by our team.",
    deck_cta: 'Open the map',

    // === SECTION: Built for your table (no testimonials until we have
    // real, consented ones) ===
    people_eyebrow: 'BUILT FOR YOUR TABLE',
    people_title: 'The moment you stop',
    people_title_em: 'apologizing',
    people_title_tail: 'for your allergies.',
    people_sub: 'Tell us what you avoid once, and every menu you chat with is checked against it before you order.',
    people_cta: 'Find a restaurant',
    // === SECTION: Pricing peek ===
    // "Either side of the table" was clever but a new visitor couldn't
    // parse it at a glance. Replaced with the literal claim — the italic
    // accent carries the parallel structure.
    peek_eyebrow: 'FREE FOREVER',
    peek_title: 'Free for diners.',
    peek_title_em: 'Free for restaurants.',
    peek_sub: "Pay when you outgrow the free plan. Not before.",
    peek_diner: 'FOR DINERS',
    peek_diner_line: '$0 → $10 → $75 / month',
    peek_diner_hint: '20 chats free. 200 with Plus. Unlimited with Premium.',
    peek_diner_cta: 'See diner pricing',
    peek_rest: 'FOR RESTAURANTS',
    peek_rest_line: '$0 → $35 → $100 / month',
    peek_rest_hint: 'Free for your listing. Growth adds analytics. Enterprise adds hands-on support.',
    peek_rest_cta: 'See restaurant pricing',

    // === SECTION: Final CTA ===
    final_title: 'Ready to eat with',
    final_title_em: 'confidence?',
    final_sub: 'Free to try. No credit card. Works on every phone.',
    final_cta_primary: 'Try it free',
    final_cta_secondary: 'I run a restaurant',
  },
  onboarding: {
    step_of: 'STEP {step} OF 3',
    skip: 'Skip for now',
    back: 'Back',
    continue: 'Continue',
    finish: 'Finish & go to dashboard',
    error_save: 'Something went wrong saving. Try again.',
    // Step 1 — allergies
    s1_eyebrow: 'SAFETY FIRST',
    s1_title: 'What do you need to avoid?',
    s1_body: 'Chat uses your allergies on every menu to flag unsafe items. Tap the ones that apply — add severity so we know when to really dig in.',
    s1_common_eyebrow: 'COMMON ALLERGENS',
    s1_custom_placeholder: 'Other (e.g. lupin, celery, mango)…',
    s1_add: 'Add',
    s1_your_list: 'YOUR ALLERGIES ({count}) — SET SEVERITY',
    // Step 2 — taste
    s2_eyebrow: 'YOUR TASTE',
    s2_title: 'What do you love to eat?',
    s2_body: 'No wrong answers. Pick a dietary style if you have one, plus your favorite cuisines — chat will rank recommendations around them.',
    s2_diet_eyebrow: 'DIETARY STYLE',
    s2_cuisines_eyebrow: 'FAVORITE CUISINES',
    // Step 3 — context
    s3_eyebrow: 'HOW YOU DINE',
    s3_title: 'Who are we cooking this up for?',
    s3_body: 'A quick snapshot so chat suggests the right size, price, and vibe.',
    s3_who_eyebrow: 'YOU MOSTLY DINE',
    s3_how_often_eyebrow: 'HOW OFTEN YOU EAT OUT',
    s3_ambition_eyebrow: 'YOUR DINING MOOD',
  },
  about: {
    eyebrow: 'ABOUT',
    headline_l1: "Dining out shouldn't come",
    headline_emphasis: 'with a disclaimer',
    sub: 'Chat With Menu exists for everyone who has ever apologized for their allergy, squinted at an ingredient list at the table, or eaten a boring salad because "safe" was the only option.',
    story_eyebrow: 'THE STORY',
    story_title: 'We started with one bad dinner.',
    story_p1: "A family of four. A new restaurant. A kid with a peanut allergy. A server who wasn't sure. A manager who made a guess. A trip to the hospital that (thankfully) ended fine, but didn't have to happen.",
    story_p2: "The problem wasn't that the restaurant didn't care — it was that the information every party needed (the diner's allergies, the dish's ingredients, the kitchen's handling) didn't live in the same place. We built Chat With Menu so it can.",
    values_eyebrow: 'WHAT WE BUILD FOR',
    values_title: 'Our principles',
    v1_title: 'Safety first, always',
    v1_body: "Wrong answer on an allergy is never acceptable. We build for the one-in-a-thousand edge case because for that diner, it's the only case.",
    v2_title: 'Trust is earned',
    v2_body: "Every allergen tag, every dish recommendation, every confidence score — backed by the restaurant's actual data, not guesses.",
    v3_title: 'Community signals',
    v3_body: "Diners share what worked, restaurants share what they're accommodating. The table gets smarter every time someone dines.",
    v4_title: 'Dining should feel good',
    v4_body: "Anyone with a restriction knows the pit-in-your-stomach feeling at a new restaurant. We're here to turn that into delight.",
    timeline_eyebrow: 'TIMELINE',
    timeline_title: "Where we've been",
    ms1_label: 'Idea',
    ms1_body:  'Founded after a bad night out with a peanut-allergic kid in a city full of unlabeled menus.',
    ms2_label: 'Launch',
    ms2_body:  'Chat With Menu launches: ask any menu about your allergies and diet.',
    ms3_label: 'Scale',
    ms3_body:  '{count} restaurants on Chat With Menu, and counting.',
    cta_headline_l1: 'Eat with',
    cta_headline_emphasis: 'confidence',
    cta_body: 'Join diners using Chat With Menu to discover restaurants that take their table seriously.',
    cta_primary: 'Get started free',
    cta_secondary: 'See how it works',
  },
  how: {
    eyebrow: 'HOW IT WORKS',
    headline_l1: 'Dining with allergies,',
    headline_emphasis: 'simplified',
    sub: 'Three steps for diners. Three steps for restaurants. Everyone eats with confidence.',
    track_diner: 'For diners',
    track_restaurant: 'For restaurants',
    // Diner track
    d1_title: 'Tell us your table',
    d1_body: 'Add allergies, preferences, and anyone you dine with. Your profile travels with you — no repeating yourself at every restaurant.',
    d2_title: 'Chat with any menu',
    d2_body: "Ask \"what's safe for my daughter's peanut allergy?\" and get ranked picks in seconds. Backed by the restaurant's actual ingredients — not guesses.",
    d3_title: 'Order with confidence',
    d3_body: 'Save what worked, share safe picks with your table, and reorder without starting from scratch next time.',
    // Restaurant track
    r1_title: 'Drop in your menu',
    r1_body: 'Paste a menu or upload your docs. Our AI tags allergens and dietary attributes automatically — you review and publish.',
    r2_title: 'Your staff looks smart',
    r2_body: 'Servers get fast allergy lookups on any dish in seconds. Zero training. Every guest question answered correctly.',
    r3_title: 'Diners find you',
    r3_body: 'Allergy-conscious diners use Chat With Menu to discover restaurants that take their needs seriously. Word spreads fast.',
    // Stats
    stat3_label: 'restaurants on the platform',
    cta_title: 'Ready to eat with confidence?',
    cta_primary: 'Get started free',
    cta_secondary: 'Browse restaurants',
  },
  toasts: {
    profile_saved: 'Profile saved.',
    link_copied: 'Link copied.',
    menu_saved: 'Menu saved.',
    review_submitted: 'Review submitted. Thanks!',
  },
};

// Spanish
const es = {
  common: {
    save: 'Guardar', cancel: 'Cancelar', close: 'Cerrar', back: 'Atrás', continue: 'Continuar',
    yes: 'Sí', no: 'No', loading: 'Cargando…', saving: 'Guardando…', saved: 'Guardado.',
    edit: 'Editar', delete: 'Eliminar', add: 'Añadir', remove: 'Quitar', done: 'Listo',
    search: 'Buscar', filter: 'Filtrar', clear: 'Limpiar', apply: 'Aplicar',
    next: 'Siguiente', previous: 'Anterior', skip: 'Saltar', submit: 'Enviar',
    confirm: 'Confirmar', required: 'Obligatorio', optional: 'Opcional',
    error: 'Algo salió mal.', retry: 'Reintentar',
  },
  nav: {
    discover: 'Descubrir',
    for_restaurants: 'Para Negocios',
    pricing: 'Precios',
    dashboard: 'Panel',
    notifications: 'Notificaciones',
    sign_in: 'Iniciar sesión',
    sign_out: 'Cerrar sesión',
    get_started: 'Empezar',
    home_aria: 'Inicio',
  },
  footer: {
    how_it_works: 'Cómo funciona',
    about: 'Nosotros',
    billing: 'Facturación',
    settings: 'Ajustes',
    terms: 'Términos',
    privacy: 'Privacidad',
    contact: 'Contacto',
    copyright: '© {year} Chat With Menu',
  },
  auth: {
    sign_in_title: 'Bienvenido de vuelta',
    sign_up_title: 'Crea tu cuenta',
    sign_in_sub: 'Inicia sesión para continuar donde lo dejaste.',
    sign_up_sub: 'Guarda restaurantes, chatea con menús, gana puntos.',
    tab_sign_in: 'Iniciar sesión',
    tab_sign_up: 'Crear cuenta',
    full_name: 'Nombre completo',
    name_placeholder: 'Tu nombre',
    email: 'Correo',
    email_placeholder: 'tu@ejemplo.com',
    password: 'Contraseña',
    password_placeholder: 'Al menos 8 caracteres',
    show: 'Mostrar',
    hide: 'Ocultar',
    show_password: 'Mostrar contraseña',
    hide_password: 'Ocultar contraseña',
    continue_with_google: 'Continuar con Google',
    continue_with_apple: 'Continuar con Apple',
    or_email: 'o continuar con correo',
    no_account: '¿No tienes cuenta?',
    have_account: '¿Ya tienes cuenta?',
    forgot: '¿Olvidaste tu contraseña?',
    submit_sign_in: 'Iniciar sesión',
    submit_sign_up: 'Crear cuenta',
    submitting_sign_in: 'Iniciando sesión…',
    submitting_sign_up: 'Creando cuenta…',
    err_invalid: 'Credenciales inválidas. Inténtalo de nuevo.',
    err_network: 'No se pudo conectar al servidor. Verifica tu conexión e inténtalo de nuevo.',
    err_oauth_soon: 'El inicio con Google y Apple estará listo pronto. Por ahora continúa con correo.',
  },
  settings: {
    eyebrow: 'AJUSTES',
    title_l1: 'Ajusta cómo',
    title_l2: 'te contactamos',
    subtitle: 'Notificaciones, privacidad, idioma. Para perfil alimentario, ve a Panel → Alergias.',
    channels_eyebrow: 'CÓMO TE CONTACTAMOS',
    channels_title: 'Canales de notificación',
    push: 'Notificaciones push',
    push_hint: 'En este dispositivo y la app móvil.',
    email: 'Correo',
    email_hint: 'Los recibos de facturación llegan sí o sí.',
    sms: 'SMS',
    sms_hint: 'Solo alertas urgentes. Se aplican tarifas estándar.',
    events_eyebrow: 'TIPOS DE EVENTO',
    events_title: 'Sobre qué notificarme',
    cat_table_invites: 'Invitaciones a la mesa',
    cat_table_invites_hint: 'Alguien pide unirse a tu mesa, o acepta tu invitación',
    cat_safety_alerts: 'Alertas de seguridad',
    cat_safety_alerts_hint: 'Reacciones, retiros o nuevos riesgos de alérgenos donde te interesa',
    cat_dish_matches: 'Platos para ti',
    cat_dish_matches_hint: 'Nuevos platos seguros que coinciden con tu perfil',
    cat_order_updates: 'Actualizaciones de pedido',
    cat_order_updates_hint: 'Aceptado, listo para recoger, entregado',
    cat_reviews_on_me: 'Respuestas a tus reseñas',
    cat_reviews_on_me_hint: 'Los restaurantes responden a algo que publicaste',
    cat_billing: 'Facturación',
    cat_billing_hint: 'Facturas, cambios de plan, pagos fallidos',
    cat_product_updates: 'Novedades del producto',
    cat_product_updates_hint: 'Nuevas funciones (bajo volumen)',
    cat_weekly_digest: 'Resumen semanal',
    cat_weekly_digest_hint: 'Domingo: nuevos sitios + notas de tu mesa',
    quiet_eyebrow: 'NO ME MOLESTES',
    quiet_title: 'Horas tranquilas',
    quiet_toggle: 'Silenciar avisos no críticos por la noche',
    quiet_hint: 'Las alertas de seguridad siempre llegan.',
    quiet_from: 'DESDE',
    quiet_to: 'HASTA',
    privacy_eyebrow: 'LO QUE VEN OTROS',
    privacy_title: 'Privacidad',
    priv_share_prefs: 'Compartir mi perfil alimentario con restaurantes al chatear',
    priv_share_prefs_hint: 'Deja que los meseros vean tus restricciones antes.',
    priv_attribute_signals: 'Firmar mis señales de seguridad públicamente',
    priv_attribute_signals_hint: 'Desactivado = tus señales son anónimas.',
    priv_show_review_name: 'Mostrar mi nombre en reseñas',
    priv_show_review_name_hint: "Desactivado = publicado como 'Comensal anónimo'.",
    priv_allow_invites: 'Permitir invitaciones de amigos a tu mesa',
    priv_allow_invites_hint: 'Desactivado = solo las que tú comienzas.',
    locale_eyebrow: 'REGIÓN',
    locale_title: 'Idioma y moneda',
    language: 'Idioma',
    currency: 'Moneda',
    measurements: 'Medidas',
    imperial: 'Imperial (fl oz, °F)',
    metric: 'Métrico (ml, °C)',
    danger_eyebrow: 'ZONA DE PELIGRO',
    danger_title: 'Acciones de cuenta',
    reset: 'Restablecer ajustes predeterminados',
    reset_hint: 'Notificaciones, privacidad, región. No afecta perfil alimentario ni restaurantes guardados.',
    reset_action: 'Restablecer',
    download: 'Descargar mis datos',
    download_hint: 'Preferencias, reseñas, lugares guardados — en JSON. Llega por correo en 24 h.',
    download_action: 'Solicitar',
    delete_account: 'Eliminar cuenta',
    delete_account_hint: 'Borra tu perfil, reseñas y señales. No se puede deshacer.',
    delete_action: 'Eliminar',
  },
  chat: {
    empty_title: 'Chatea con el menú',
    empty_subtitle: 'Pregunta sobre alérgenos, maridajes o qué puede personalizar la cocina — puedes ser específico.',
    gate_no_restaurant_title: 'Elige un restaurante primero',
    gate_no_restaurant_sub: 'Abre el chat desde una tarjeta de restaurante para saber con qué menú ayudarte.',
    gate_no_restaurant_cta: 'Explorar restaurantes',
    gate_signin_title: 'Inicia sesión para chatear',
    gate_signin_sub: 'Guarda tu conversación, elige platos y desbloquea señales de seguridad.',
    gate_signin_cta: 'Iniciar sesión',
    input_placeholder: 'Pregunta sobre un alérgeno, un plato o el ambiente…',
    send: 'Enviar',
    send_failed: 'Fallo al enviar. Reintenta.',
    ai_offline: 'El asistente está recuperando el aliento. Reintenta en un momento.',
    reset_chat: 'Nueva conversación',
    suggested_prompts_label: 'Prueba preguntar',
    suggest_allergens: '¿Qué hay sin gluten y sin lácteos en este menú?',
    suggest_recommend: '¿De qué está más orgullosa la cocina esta noche?',
    suggest_pair: '¿Qué marida bien con el salmón?',
    suggest_cross: '¿Tienen una estación sin cacahuates?',
  },
  discover: {
    hero_title: 'Restaurantes que tu cuerpo agradecerá',
    hero_subtitle: 'Desliza a la derecha para guardar, izquierda para saltar — aprenderemos qué funciona para ti.',
    brand_tagline: 'Descubrir restaurantes',
    auto_banner_title: 'Filtrado desde tu perfil',
    auto_banner_body: ' — mostrando sitios que coinciden con {tags}.',
    clear_auto: 'Mostrar todo',
    result_count: '{shown} de {total} sitios',
    empty: 'Nada coincide con estos filtros. Prueba quitar una etiqueta.',
  },
  dashboard: {
    welcome: 'Bienvenido, {name}',
    welcome_fallback: 'Bienvenido…',
    subtitle_diner:    'Navega entre descubrimiento, familia y herramientas de mesa sin perder el hilo.',
    subtitle_merchant: 'Gestiona tus restaurantes, menús y señales de clientes.',
    subtitle_server:   'Consultas rápidas de alergias y especiales mientras atiendes.',
    role_diner:   'Comensal',
    role_admin:   'Admin',
    role_owner:   'Propietario',
    role_server:  'Mesero',
    tab_allergies:   'Alergias',
    tab_preferences: 'Preferencias',
    tab_profile:     'Editar perfil',
    tab_family:      'Familia',
    tab_table:       'Mi mesa',
    tab_history:     'Historial',
    tab_rewards:     'Recompensas',
    tab_discover:    'Descubrir',
    tab_operations:  'Operaciones',
    tab_menus:       'Gestionar menús',
    tab_add_rest:    'Añadir restaurante',
    tab_service:     'Herramientas',
    panel_discovery_eyebrow: 'Descubrimiento',
    panel_discovery_title:   'Encuentra lugares que se ajusten a tu mesa',
    panel_discovery_sub:     'Desliza, compara y cambia a vista de mapa sin salir del panel.',
    panel_profile_eyebrow:   'Perfil',
    panel_profile_title:     'Tu identidad + necesidades',
    panel_profile_sub:       'Datos de la cuenta más estilo dietético, necesidades médicas, elecciones éticas y preferencias sensoriales.',
    panel_allergies_eyebrow: 'Alergias',
    panel_allergies_title:   'Lleva registro de los ingredientes que importan',
    panel_allergies_sub:     'Actualiza tus alergias para que el chat y las recomendaciones sean confiables.',
    panel_preferences_eyebrow: 'Preferencias',
    panel_preferences_title: 'Enséñale a la app lo que te gusta',
    panel_preferences_sub:   'Notas de voz, preferencias de comida y señales de gusto deben sentirse personales.',
    panel_family_eyebrow:    'Familia',
    panel_family_title:      'Administra a las personas que proteges',
    panel_family_sub:        'Perfiles compartidos y contexto dietético en un solo lugar.',
    panel_table_eyebrow:     'Mi mesa',
    panel_table_title:       'Construye tu red de apoyo al comer',
    panel_table_sub:         'Conexiones, invitaciones, descubrimiento y seguridad juntos.',
    panel_rewards_eyebrow:   'Recompensas',
    panel_rewards_title:     'Puntos, beneficios y impulso al comer',
    panel_rewards_sub:       'Las recompensas llegarán pronto. Aún no hay nada para canjear.',
  },
  dish: {
    back: 'Atrás',
    menu_fallback: 'MENÚ',
    needs_review: 'Necesita revisión',
    ask_about_this: 'Preguntar sobre esto',
    next_step_eyebrow: 'SIGUIENTE PASO',
    next_step_title: '¿No estás seguro si este plato es seguro para ti?',
    next_step_body: 'Chatea con el menú de {name} — verificaremos tus alergias en un toque.',
    share_safety_cta: 'Compartir señal',
    chat_about: 'Chatear sobre {name}',
    allergen_eyebrow: 'DETALLES DE ALÉRGENOS',
    allergen_clear_title: 'Sin alérgenos marcados',
    allergen_clear_body: 'Siempre confirma con tu mesero si tienes alergia severa.',
    allergen_count: '{count} alérgenos a tener en cuenta',
    allergen_count_one: '1 alérgeno a tener en cuenta',
    allergen_hint: 'Severidad inferida de las etiquetas, métodos de preparación e ingredientes removibles.',
    sev_contains: 'Contiene',
    sev_prepared: 'Preparado con',
    sev_may_contain: 'Puede contener',
    sev_critical: 'Crítico',
    prep_eyebrow: 'PREPARACIÓN',
    prep_title: 'Cómo se hace',
    prep_empty: 'Aún sin detalles. Pregunta a la cocina — ellos saben.',
    prep_methods_eyebrow: 'MÉTODOS',
    prep_removable_eyebrow: 'SE PUEDE QUITAR',
    prep_mods_eyebrow: 'PERSONALIZACIONES',
    prep_required: 'obligatorio',
    pair_eyebrow: 'VA BIEN CON',
    pair_title: 'Arma una comida completa',
    pair_empty: 'Sugeriremos maridajes cuando el menú tenga más platos.',
    pair_drink: 'Para beber',
    pair_start: 'Para empezar',
    pair_finish: 'Para terminar',
    pair_side: 'Acompañamiento',
    similar_eyebrow_prefix: 'MÁS DE',
    similar_menu_fallback: 'ESTE MENÚ',
    similar_title: 'Platos similares',
    similar_no_allergens: 'Sin alérgenos marcados',
    similar_n_allergens: '{n} alérgenos',
    similar_one_allergen: '1 alérgeno',
    loading: 'Cargando plato…',
    not_found_title: 'Plato no encontrado',
    not_found_body: 'Puede haber sido eliminado del menú, o el enlace es incorrecto.',
    not_found_cta: 'Atrás',
  },
  restaurant: {
    write_review: 'Escribir reseña',
    chat_cta: 'Chatear con el menú',
    safety_signal_cta: 'Compartir señal de seguridad',
    ask_table_cta: 'Preguntar a la mesa',
    dietary_eyebrow: 'LO QUE ATIENDEN',
    menu_highlights: 'DESTACADOS DEL MENÚ',
    contact_eyebrow: 'CONTACTO',
    contact_none: 'Sin información de contacto publicada.',
    safety_eyebrow: 'SEÑALES DE SEGURIDAD',
    safety_body: 'Platos seguros verificados por la comunidad. Comparte el tuyo y ayuda a la mesa.',
    view_signals: 'Ver señales',
    see_full_menu: 'Ver menú completo en el chat',
    no_menu: 'Aún no hay menú publicado. Chatea con el restaurante para opciones al día.',
    reviews_title: 'Qué dicen los comensales',
    reviews_eyebrow: 'RESEÑAS RECIENTES',
    reviews_count: '{count} reseñas',
    reviews_safety_eyebrow: 'SEGURIDAD ANTE ALERGIAS',
    reviews_safety_hint: 'Cuánta confianza sintieron los comensales al preguntar sobre alérgenos.',
    reviews_see_all: 'Ver las {count} reseñas',
    loading: 'Cargando restaurante…',
    not_found_title: 'Restaurante no encontrado',
    not_found_body: 'Puede haber sido eliminado, o el enlace es incorrecto.',
    not_found_cta: 'Ver restaurantes',
    time_today: 'hoy',
    time_yesterday: 'ayer',
    time_days_ago: 'hace {n} días',
    time_week_ago: 'hace 1 semana',
    time_weeks_ago: 'hace {n} semanas',
    time_month_ago: 'hace 1 mes',
    time_months_ago: 'hace {n} meses',
  },
  menu_manager: {
    eyebrow: 'GESTOR DE MENÚ',
    title_l1: 'Tu menú,',
    title_l2: 'limpio y al día',
    subtitle: 'Explora y edita platos, publica especiales, ajusta tips de venta y mantén tu perfil afinado.',
    tab_items: 'Platos',
    tab_specials: 'Especiales',
    tab_upsell: 'Tips de venta',
    tab_profile: 'Perfil',
    publish: 'Publicar menú',
    publish_n: 'Publicar ({n})',
    publish_first: 'Publica tu menú',
    discard: 'Descartar borrador',
    status_live: 'En vivo',
    status_unpublished: '{n} sin publicar',
    status_not_published: 'Sin publicar',
    saved_toast: 'Menú publicado — los comensales lo ven ahora.',
    reverted_toast: 'Revertido a la versión en vivo.',
  },
  safety: {
    eyebrow: 'SEÑALES DE SEGURIDAD',
    title_l1: 'Sabe qué es',
    title_l2: 'realmente seguro',
    subtitle: 'Reportes comunitarios de platos seguros, calificados por quién lo confirmó — mesero, cocina o experiencia vivida.',
    share_cta: 'Comparte una señal',
    filter_label: 'Filtrar',
    filter_all: 'Todos',
    filter_worked: '✓ Funcionó',
    filter_reactions: '⚠ Reacciones',
    filter_all_restaurants: 'Todos los restaurantes',
    filter_all_restrictions: 'Todas las restricciones',
    results_count: '{shown} de {total} señales',
    empty_title: 'Aún no hay señales para este filtro',
    empty_body: 'Sé el primero en compartir. Un solo plato confirmado ayuda a decenas de comensales a decidir.',
    load_failed: 'Error al cargar señales.',
    type_worked: 'Funcionó bien',
    type_worked_hint: 'Plato seguro para recomendar',
    type_wrong: 'Salió mal',
    type_wrong_hint: 'Reportar una reacción',
    severity: 'Severidad',
    sev_mild: 'Leve',
    sev_mild_hint: 'Picazón, molestia leve',
    sev_moderate: 'Moderada',
    sev_moderate_hint: 'Ronchas, malestar estomacal',
    sev_severe: 'Severa',
    sev_severe_hint: 'Hinchazón, respiración, vómitos',
    sev_life: 'Potencialmente mortal',
    sev_life_hint: 'Anafilaxia, visita a urgencias',
    share_dialog_eyebrow: 'COMPARTE UNA SEÑAL',
    share_dialog_title_positive: '¿Qué te funcionó?',
    share_dialog_title_negative: 'Reporta una reacción',
    field_restaurant: 'Restaurante',
    restaurant_placeholder: 'Elige un restaurante…',
    field_dish: 'Nombre del plato',
    dish_placeholder: 'ej. Pappardelle sin gluten',
    field_restrictions: 'Restricciones cumplidas',
    what_worked_label: 'Qué funcionó',
    what_happened_label: 'Qué pasó',
    what_worked_placeholder: 'La cocina preparó una estación dedicada sin gluten con aceite de oliva y verduras frescas…',
    what_happened_placeholder: 'Le dije al mesero que soy alérgico a los lácteos, pero el plato llegó con salsa de crema. La cocina no lo marcó.',
    add_photo: 'Añadir foto (opcional)',
    notes_label: 'Notas adicionales (opcional)',
    notes_placeholder: 'El personal sabía mucho sobre contaminación cruzada…',
    confidence: 'Confianza',
    evidence: 'Evidencia',
    anonymous: 'Compartir de forma anónima',
    cancel: 'Cancelar',
    submit: 'Compartir señal',
    err_pick_restaurant: 'Elige un restaurante.',
    err_name_dish: 'Nombra el plato.',
    err_pick_restriction: 'Elige al menos una restricción.',
    err_describe_worked: 'Describe qué funcionó.',
    err_describe_happened: 'Describe qué pasó.',
    err_pick_severity: 'Elige un nivel de severidad.',
    err_share_failed: 'Error al compartir señal.',
    share_success: 'Señal compartida — gracias.',
    photo_drop_hint: 'Haz clic o arrastra una foto del plato, menú o recibo',
    photo_drop_active: 'Suelta la foto aquí',
    photo_size_note: 'JPG, PNG, WebP, HEIC o GIF — hasta 10 MB',
    photo_replace: 'Reemplazar',
    photo_remove: 'Quitar',
    photo_not_image: 'No es una imagen',
    photo_too_large: 'Muy grande (máx 10 MB)',
    reaction_report: 'Reporte de reacción',
    card_by: 'por {name}',
    card_anon: 'compartido anónimamente',
    evidence_menu_label: 'Etiqueta del menú',
    evidence_server_confirmed: 'Confirmado por mesero',
    evidence_kitchen_confirmed: 'Confirmado por cocina',
    evidence_user_experience: 'Experiencia del usuario',
    ver_unverified: 'Sin verificar',
    ver_restaurant: 'Verificado por restaurante',
    ver_staff: 'Verificado por personal',
    ver_kitchen: 'Confirmado por cocina',
  },
  landing: {
    eyebrow: 'Descubrimiento con IA',
    headline_l1: 'Nunca te estreses',
    headline_l2: 'por',
    headline_emphasis: 'qué comer',
    sub: 'Chatea con el menú de cualquier restaurante. Recibe recomendaciones adaptadas a tus alergias, dieta y tu estado de ánimo. Aprenderemos qué te funciona.',
    pick_eyebrow: 'Elige lo que te importa',
    pick_tag_in: 'Elige una etiqueta de {category}',
    cta_primary: 'Empezar a descubrir',
    cta_secondary: 'Ver demo',
    preview_example: 'Vista de ejemplo',
    stat_restaurants: 'Restaurantes',
    group_religious:              'Religiosas',
    group_diet:                   'Dieta',
    group_allergies:              'Alergias',
    group_therapeutic:            'Terapéuticas',
    group_lifestyle:              'Estilo de vida',
    group_ethical:                'Éticas',
    group_life_stage:             'Etapa de vida',
    group_glp1_portions:          'GLP-1 / Porciones',
    group_sensory_neurodivergent: 'Sensorial / Neurodivergente',
  },
  onboarding: {
    step_of: 'PASO {step} DE 3',
    skip: 'Omitir por ahora',
    back: 'Atrás',
    continue: 'Continuar',
    finish: 'Finalizar e ir al panel',
    error_save: 'Algo salió mal al guardar. Reintenta.',
    s1_eyebrow: 'LA SEGURIDAD PRIMERO',
    s1_title: '¿Qué necesitas evitar?',
    s1_body: 'El chat usa tus alergias en cada menú para marcar platos inseguros. Toca las que apliquen y añade severidad para que sepamos cuándo profundizar.',
    s1_common_eyebrow: 'ALÉRGENOS COMUNES',
    s1_custom_placeholder: 'Otro (ej. lupino, apio, mango)…',
    s1_add: 'Añadir',
    s1_your_list: 'TUS ALERGIAS ({count}) — AJUSTA LA SEVERIDAD',
    s2_eyebrow: 'TU PALADAR',
    s2_title: '¿Qué te encanta comer?',
    s2_body: 'No hay respuestas equivocadas. Elige tu estilo dietético si tienes uno, además de tus cocinas favoritas — el chat rankeará según eso.',
    s2_diet_eyebrow: 'ESTILO DIETÉTICO',
    s2_cuisines_eyebrow: 'COCINAS FAVORITAS',
    s3_eyebrow: 'CÓMO CENAS',
    s3_title: '¿Para quién cocinamos todo esto?',
    s3_body: 'Un vistazo rápido para que el chat sugiera el tamaño, precio y vibra correctos.',
    s3_who_eyebrow: 'GENERALMENTE CENAS',
    s3_how_often_eyebrow: 'FRECUENCIA CON QUE COMES FUERA',
    s3_ambition_eyebrow: 'TU HUMOR CULINARIO',
  },
  about: {
    eyebrow: 'NOSOTROS',
    headline_l1: 'Comer fuera no debería venir',
    headline_emphasis: 'con un descargo',
    sub: 'Chat With Menu existe para cada persona que alguna vez se disculpó por su alergia, entrecerró los ojos leyendo ingredientes en la mesa, o comió una ensalada aburrida porque "seguro" era la única opción.',
    story_eyebrow: 'LA HISTORIA',
    story_title: 'Empezamos con una mala cena.',
    story_p1: 'Una familia de cuatro. Un restaurante nuevo. Un niño con alergia al maní. Un mesero que no estaba seguro. Un gerente que adivinó. Un viaje al hospital que (por suerte) terminó bien, pero que no tenía que pasar.',
    story_p2: 'El problema no era que al restaurante no le importara — era que la información que cada parte necesitaba (las alergias del comensal, los ingredientes del plato, el manejo en cocina) no vivía en el mismo lugar. Construimos Chat With Menu para que así sea.',
    values_eyebrow: 'PARA QUÉ CONSTRUIMOS',
    values_title: 'Nuestros principios',
    v1_title: 'La seguridad primero, siempre',
    v1_body: 'Una respuesta equivocada sobre una alergia nunca es aceptable. Construimos para ese caso uno-en-mil porque para ese comensal, es el único caso.',
    v2_title: 'La confianza se gana',
    v2_body: 'Cada etiqueta de alérgeno, cada recomendación, cada puntaje de confianza — respaldados por los datos reales del restaurante, no por suposiciones.',
    v3_title: 'Señales de comunidad',
    v3_body: 'Los comensales comparten qué funcionó, los restaurantes qué están acomodando. La mesa se vuelve más inteligente con cada cena.',
    v4_title: 'Cenar debería sentirse bien',
    v4_body: 'Cualquiera con una restricción conoce esa sensación en el estómago al llegar a un restaurante nuevo. Estamos aquí para convertirla en alegría.',
    timeline_eyebrow: 'HISTORIA',
    timeline_title: 'Dónde hemos estado',
    ms1_label: 'Idea',
    ms1_body:  'Fundado tras una mala noche con un niño alérgico al maní en una ciudad llena de menús sin etiquetar.',
    ms2_label: 'Lanzamiento',
    ms2_body:  'Lanzamiento de Chat With Menu: pregúntale a cualquier menú por tus alergias y tu dieta.',
    ms3_label: 'Escala',
    ms3_body:  '{count} restaurantes en Chat With Menu, y sumando.',
    cta_headline_l1: 'Come con',
    cta_headline_emphasis: 'confianza',
    cta_body: 'Únete a los comensales que usan Chat With Menu para descubrir restaurantes que se toman en serio su mesa.',
    cta_primary: 'Empieza gratis',
    cta_secondary: 'Ver cómo funciona',
  },
  how: {
    eyebrow: 'CÓMO FUNCIONA',
    headline_l1: 'Cenar con alergias,',
    headline_emphasis: 'simplificado',
    sub: 'Tres pasos para comensales. Tres pasos para restaurantes. Todos comen con confianza.',
    track_diner: 'Para comensales',
    track_restaurant: 'Para restaurantes',
    d1_title: 'Cuéntanos de tu mesa',
    d1_body: 'Añade alergias, preferencias y con quién cenas. Tu perfil viaja contigo — no repitas todo en cada restaurante.',
    d2_title: 'Chatea con cualquier menú',
    d2_body: 'Pregunta "¿qué es seguro para la alergia al maní de mi hija?" y recibe opciones ranqueadas en segundos. Respaldado por los ingredientes reales del restaurante — no adivinanzas.',
    d3_title: 'Pide con confianza',
    d3_body: 'Guarda lo que funcionó, comparte opciones seguras con tu mesa y vuelve a pedir sin empezar desde cero la próxima vez.',
    r1_title: 'Sube tu menú',
    r1_body: 'Pega un menú o sube tus documentos. Nuestra IA etiqueta alérgenos y atributos dietéticos automáticamente — tú revisas y publicas.',
    r2_title: 'Tu equipo luce experto',
    r2_body: 'Los meseros consultan alergias en cualquier plato en segundos. Cero entrenamiento. Cada pregunta de cliente respondida correctamente.',
    r3_title: 'Los comensales te encuentran',
    r3_body: 'Los comensales con restricciones usan Chat With Menu para descubrir restaurantes que toman sus necesidades en serio. Se corre la voz rápido.',
    stat3_label: 'restaurantes en la plataforma',
    cta_title: '¿Listo para comer con confianza?',
    cta_primary: 'Empieza gratis',
    cta_secondary: 'Ver restaurantes',
  },
  toasts: {
    profile_saved: 'Perfil guardado.',
    link_copied: 'Enlace copiado.',
    menu_saved: 'Menú guardado.',
    review_submitted: '¡Reseña enviada. Gracias!',
  },
};

// French
const fr = {
  common: {
    save: 'Enregistrer', cancel: 'Annuler', close: 'Fermer', back: 'Retour', continue: 'Continuer',
    yes: 'Oui', no: 'Non', loading: 'Chargement…', saving: 'Enregistrement…', saved: 'Enregistré.',
    edit: 'Modifier', delete: 'Supprimer', add: 'Ajouter', remove: 'Retirer', done: 'Terminé',
    search: 'Rechercher', filter: 'Filtrer', clear: 'Effacer', apply: 'Appliquer',
    next: 'Suivant', previous: 'Précédent', skip: 'Passer', submit: 'Envoyer',
    confirm: 'Confirmer', required: 'Obligatoire', optional: 'Facultatif',
    error: "Une erreur s'est produite.", retry: 'Réessayer',
  },
  nav: {
    discover: 'Découvrir',
    for_restaurants: 'Professionnels',
    pricing: 'Tarifs',
    dashboard: 'Tableau de bord',
    notifications: 'Notifications',
    sign_in: 'Se connecter',
    sign_out: 'Se déconnecter',
    get_started: 'Commencer',
    home_aria: 'Accueil',
  },
  footer: {
    how_it_works: 'Comment ça marche',
    about: 'À propos',
    billing: 'Facturation',
    settings: 'Paramètres',
    terms: 'Conditions',
    privacy: 'Confidentialité',
    contact: 'Contact',
    copyright: '© {year} Chat With Menu',
  },
  auth: {
    sign_in_title: 'Bon retour',
    sign_up_title: 'Créez votre compte',
    sign_in_sub: 'Connectez-vous pour reprendre là où vous en étiez.',
    sign_up_sub: "Enregistrez des restaurants, chattez avec les menus, gagnez des points.",
    tab_sign_in: 'Se connecter',
    tab_sign_up: 'Créer un compte',
    full_name: 'Nom complet',
    name_placeholder: 'Votre nom',
    email: 'E-mail',
    email_placeholder: 'vous@exemple.com',
    password: 'Mot de passe',
    password_placeholder: 'Au moins 8 caractères',
    show: 'Afficher',
    hide: 'Masquer',
    show_password: 'Afficher le mot de passe',
    hide_password: 'Masquer le mot de passe',
    continue_with_google: 'Continuer avec Google',
    continue_with_apple: 'Continuer avec Apple',
    or_email: 'ou continuer avec un e-mail',
    no_account: "Pas de compte ?",
    have_account: 'Déjà un compte ?',
    forgot: 'Mot de passe oublié ?',
    submit_sign_in: 'Se connecter',
    submit_sign_up: 'Créer un compte',
    submitting_sign_in: 'Connexion…',
    submitting_sign_up: 'Création du compte…',
    err_invalid: 'Identifiants invalides. Réessayez.',
    err_network: 'Impossible de joindre le serveur. Vérifiez votre connexion.',
    err_oauth_soon: 'La connexion Google et Apple arrive bientôt. Continuez avec votre e-mail pour le moment.',
  },
  settings: {
    eyebrow: 'PARAMÈTRES',
    title_l1: 'Ajustez comment',
    title_l2: 'on vous contacte',
    subtitle: 'Notifications, confidentialité, langue. Pour le profil alimentaire, voir Tableau de bord → Allergies.',
    channels_eyebrow: 'COMMENT ON VOUS CONTACTE',
    channels_title: 'Canaux de notification',
    push: 'Notifications push',
    push_hint: 'Sur cet appareil et dans l\'app mobile.',
    email: 'E-mail',
    email_hint: 'Les reçus de facturation arrivent toujours.',
    sms: 'SMS',
    sms_hint: 'Alertes urgentes uniquement. Tarifs standard appliqués.',
    events_eyebrow: "TYPES D'ÉVÉNEMENTS",
    events_title: 'De quoi me notifier',
    cat_table_invites: 'Invitations à table',
    cat_table_invites_hint: 'Quelqu\'un veut rejoindre votre table, ou accepte votre invitation',
    cat_safety_alerts: 'Alertes sécurité',
    cat_safety_alerts_hint: 'Réactions, rappels ou nouveaux risques d\'allergènes dans les lieux suivis',
    cat_dish_matches: 'Plats pour vous',
    cat_dish_matches_hint: 'Nouveaux plats sûrs qui correspondent à votre profil',
    cat_order_updates: 'Suivi de commande',
    cat_order_updates_hint: 'Accepté, prêt, livré',
    cat_reviews_on_me: 'Réponses à vos avis',
    cat_reviews_on_me_hint: 'Les restaurants répondent à quelque chose que vous avez publié',
    cat_billing: 'Facturation',
    cat_billing_hint: 'Factures, changements de plan, paiements échoués',
    cat_product_updates: 'Nouveautés du produit',
    cat_product_updates_hint: 'Nouvelles fonctionnalités (peu fréquent)',
    cat_weekly_digest: 'Résumé hebdo',
    cat_weekly_digest_hint: 'Dimanche : nouveaux lieux + notes de votre table',
    quiet_eyebrow: 'NE PAS DÉRANGER',
    quiet_title: 'Heures silencieuses',
    quiet_toggle: 'Couper les notifications non urgentes la nuit',
    quiet_hint: 'Les alertes de sécurité passent toujours.',
    quiet_from: 'DE',
    quiet_to: 'À',
    privacy_eyebrow: 'CE QUE VOIENT LES AUTRES',
    privacy_title: 'Confidentialité',
    priv_share_prefs: 'Partager mon profil alimentaire avec les restaurants lors du chat',
    priv_share_prefs_hint: 'Les serveurs voient vos restrictions à l\'avance.',
    priv_attribute_signals: 'Signer mes signaux de sécurité publiquement',
    priv_attribute_signals_hint: 'Désactivé = vos signaux sont anonymes.',
    priv_show_review_name: 'Afficher mon nom sur les avis',
    priv_show_review_name_hint: "Désactivé = publié comme 'Client anonyme'.",
    priv_allow_invites: 'Autoriser les invitations de table entre amis',
    priv_allow_invites_hint: 'Désactivé = seules celles que vous lancez.',
    locale_eyebrow: 'RÉGION',
    locale_title: 'Langue et devise',
    language: 'Langue',
    currency: 'Devise',
    measurements: 'Mesures',
    imperial: 'Impérial (fl oz, °F)',
    metric: 'Métrique (ml, °C)',
    danger_eyebrow: 'ZONE DANGEREUSE',
    danger_title: 'Actions du compte',
    reset: 'Réinitialiser les paramètres',
    reset_hint: 'Notifications, confidentialité, région. Ne touche pas au profil alimentaire ni aux lieux enregistrés.',
    reset_action: 'Réinitialiser',
    download: 'Télécharger mes données',
    download_hint: 'Préférences, avis, lieux enregistrés — en JSON. Envoi par e-mail sous 24 h.',
    download_action: 'Demander',
    delete_account: 'Supprimer le compte',
    delete_account_hint: 'Efface votre profil, avis et signaux. Irréversible.',
    delete_action: 'Supprimer',
  },
  chat: {
    empty_title: 'Discutez avec le menu',
    empty_subtitle: 'Posez des questions sur les allergènes, les accords, ou ce que la cuisine peut personnaliser.',
    input_placeholder: 'Demandez un allergène, un plat, l\'ambiance…',
    send: 'Envoyer',
    send_failed: 'Échec de l\'envoi. Réessayez.',
    ai_offline: "L'assistant reprend son souffle. Réessayez dans un instant.",
    reset_chat: 'Nouvelle conversation',
    suggested_prompts_label: 'Essayez',
    suggest_allergens: "Qu'est-ce qui est sans gluten et sans lactose sur ce menu ?",
    suggest_recommend: 'De quoi la cuisine est-elle la plus fière ce soir ?',
    suggest_pair: "Qu'est-ce qui va bien avec le saumon ?",
    suggest_cross: 'Avez-vous un poste sans cacahuètes ?',
  },
  discover: {
    hero_title: 'Des restaurants qui vous font du bien',
    hero_subtitle: 'Glissez à droite pour garder, à gauche pour passer — on apprendra ce qui vous va.',
    brand_tagline: 'Découvrir les restaurants',
    auto_banner_title: 'Filtré depuis votre profil',
    auto_banner_body: ' — lieux correspondant à {tags}.',
    clear_auto: 'Tout afficher',
    result_count: '{shown} sur {total} lieux',
    empty: 'Rien ne correspond. Essayez de retirer un filtre.',
  },
  dashboard: {
    welcome: 'Bienvenue, {name}',
    welcome_fallback: 'Bienvenue…',
    subtitle_diner:    'Naviguez entre découverte, famille et outils de table sans perdre le fil.',
    subtitle_merchant: 'Gérez vos restaurants, menus et signaux clients.',
    subtitle_server:   "Consultations rapides d'allergies et de spéciaux pendant le service.",
    role_diner:   'Client',
    role_admin:   'Admin',
    role_owner:   'Propriétaire',
    role_server:  'Serveur',
    tab_allergies: 'Allergies',
    tab_preferences: 'Préférences',
    tab_profile: 'Modifier le profil',
    tab_family: 'Famille',
    tab_table: 'Ma table',
    tab_history: 'Historique',
    tab_rewards: 'Récompenses',
    tab_discover: 'Découvrir',
    tab_operations: 'Opérations',
    tab_menus:       'Gérer les menus',
    tab_add_rest:    'Ajouter un restaurant',
    tab_service:     'Outils de service',
    panel_discovery_eyebrow: 'Découverte',
    panel_discovery_title:   'Trouvez des adresses qui collent à votre table',
    panel_discovery_sub:     'Swipez, comparez, passez en vue carte sans quitter le tableau de bord.',
    panel_profile_eyebrow:   'Profil',
    panel_profile_title:     'Votre identité + besoins',
    panel_profile_sub:       'Infos de compte plus style alimentaire, besoins médicaux, choix éthiques et préférences sensorielles.',
    panel_allergies_eyebrow: 'Allergies',
    panel_allergies_title:   'Suivez les ingrédients qui comptent',
    panel_allergies_sub:     'Mettez à jour vos allergies pour que chat et recommandations restent fiables.',
    panel_preferences_eyebrow: 'Préférences',
    panel_preferences_title: "Apprenez à l'app ce que vous aimez",
    panel_preferences_sub:   'Notes vocales, préférences culinaires et signaux de goût doivent sembler personnels.',
    panel_family_eyebrow:    'Famille',
    panel_family_title:      'Gérez les gens que vous protégez',
    panel_family_sub:        'Profils partagés et contexte alimentaire au même endroit.',
    panel_table_eyebrow:     'Ma table',
    panel_table_title:       'Construisez votre réseau de soutien au dîner',
    panel_table_sub:         'Connexions, invitations, découverte et sécurité réunis.',
    panel_rewards_eyebrow:   'Récompenses',
    panel_rewards_title:     "Points, avantages et élan",
    panel_rewards_sub:       'Les récompenses arrivent bientôt. Rien à échanger pour le moment.',
  },
  dish: {
    back: 'Retour',
    menu_fallback: 'MENU',
    needs_review: 'À vérifier',
    ask_about_this: 'Poser une question',
    next_step_eyebrow: 'PROCHAINE ÉTAPE',
    next_step_title: 'Pas sûr que ce plat vous convient ?',
    next_step_body: 'Chattez avec le menu de {name} — on recoupera vos allergies en un clic.',
    share_safety_cta: 'Partager un signal',
    chat_about: 'Chatter sur {name}',
    allergen_eyebrow: 'DÉTAILS ALLERGÈNES',
    allergen_clear_title: 'Aucun allergène signalé',
    allergen_clear_body: "Confirmez toujours avec votre serveur si vous avez une allergie sévère.",
    allergen_count: "{count} allergènes à noter",
    allergen_count_one: "1 allergène à noter",
    allergen_hint: 'Sévérité déduite des étiquettes, méthodes de préparation et ingrédients retirables.',
    sev_contains: 'Contient',
    sev_prepared: 'Préparé avec',
    sev_may_contain: 'Peut contenir',
    sev_critical: 'Critique',
    prep_eyebrow: 'PRÉPARATION',
    prep_title: 'Comment c\'est fait',
    prep_empty: "Pas encore de détails. Demandez en cuisine — ils sauront.",
    prep_methods_eyebrow: 'MÉTHODES',
    prep_removable_eyebrow: 'PEUT ÊTRE RETIRÉ',
    prep_mods_eyebrow: 'PERSONNALISATIONS',
    prep_required: 'requis',
    pair_eyebrow: 'S\'ACCORDE AVEC',
    pair_title: 'Composez un repas complet',
    pair_empty: 'On suggérera des accords quand le menu aura plus de plats.',
    pair_drink: 'Boisson',
    pair_start: 'En entrée',
    pair_finish: 'En dessert',
    pair_side: 'En accompagnement',
    similar_eyebrow_prefix: 'PLUS DE',
    similar_menu_fallback: 'CE MENU',
    similar_title: 'Plats similaires',
    similar_no_allergens: 'Aucun allergène signalé',
    similar_n_allergens: '{n} allergènes',
    similar_one_allergen: '1 allergène',
    loading: 'Chargement du plat…',
    not_found_title: 'Plat introuvable',
    not_found_body: "L'article a peut-être été retiré du menu, ou le lien est incorrect.",
    not_found_cta: 'Retour',
  },
  restaurant: {
    write_review: 'Écrire un avis',
    chat_cta: 'Chatter avec le menu',
    safety_signal_cta: 'Partager un signal',
    ask_table_cta: 'Demander à la table',
    dietary_eyebrow: 'CE QU\'ILS PRENNENT EN COMPTE',
    menu_highlights: 'À LA UNE DU MENU',
    contact_eyebrow: 'CONTACT',
    contact_none: 'Pas d\'informations de contact publiées.',
    safety_eyebrow: 'SIGNAUX DE SÉCURITÉ',
    safety_body: 'Plats sûrs confirmés par la communauté. Partagez les vôtres pour aider la table.',
    view_signals: 'Voir les signaux',
    see_full_menu: 'Voir tout le menu en chat',
    no_menu: 'Aucun menu publié. Discutez avec le restaurant pour les options à jour.',
    reviews_title: 'Ce que disent les clients',
    reviews_eyebrow: 'AVIS RÉCENTS',
    reviews_count: '{count} avis',
    reviews_safety_eyebrow: 'SÉCURITÉ ALLERGIQUE',
    reviews_safety_hint: 'À quel point les clients se sont sentis en confiance pour parler allergènes.',
    reviews_see_all: 'Voir les {count} avis',
    loading: 'Chargement du restaurant…',
    not_found_title: 'Restaurant introuvable',
    not_found_body: 'Il a peut-être été retiré, ou le lien est incorrect.',
    not_found_cta: 'Parcourir les restaurants',
    time_today: "aujourd'hui",
    time_yesterday: 'hier',
    time_days_ago: 'il y a {n} jours',
    time_week_ago: 'il y a 1 semaine',
    time_weeks_ago: 'il y a {n} semaines',
    time_month_ago: 'il y a 1 mois',
    time_months_ago: 'il y a {n} mois',
  },
  menu_manager: {
    eyebrow: 'GESTION DU MENU',
    title_l1: 'Votre menu,',
    title_l2: 'propre et à jour',
    subtitle: 'Parcourez et éditez les plats, publiez des spéciaux, affinez les conseils de vente et gardez votre profil net.',
    tab_items: 'Plats',
    tab_specials: 'Spéciaux',
    tab_upsell: 'Conseils',
    tab_profile: 'Profil',
    publish: 'Publier le menu',
    publish_n: 'Publier ({n})',
    publish_first: 'Publier votre menu',
    discard: 'Jeter le brouillon',
    status_live: 'En ligne',
    status_unpublished: '{n} non publiés',
    status_not_published: 'Non publié',
    saved_toast: 'Menu publié — les clients le voient maintenant.',
    reverted_toast: 'Retour à la version en ligne.',
  },
  safety: {
    eyebrow: 'SIGNAUX DE SÉCURITÉ',
    title_l1: "Savoir ce qui est",
    title_l2: 'vraiment sûr',
    subtitle: "Rapports communautaires de plats sûrs, classés par qui l'a confirmé — serveur, cuisine, ou vécu.",
    share_cta: 'Partager un signal',
    filter_label: 'Filtrer',
    filter_all: 'Tout',
    filter_worked: '✓ A marché',
    filter_reactions: '⚠ Réactions',
    filter_all_restaurants: 'Tous les restaurants',
    filter_all_restrictions: 'Toutes les restrictions',
    results_count: '{shown} sur {total} signaux',
    empty_title: 'Aucun signal pour ce filtre',
    empty_body: "Soyez le premier à partager. Un seul plat confirmé aide des dizaines de clients à décider.",
    load_failed: 'Échec du chargement des signaux.',
    type_worked: 'Ce qui a marché',
    type_worked_hint: 'Plat sûr à recommander',
    type_wrong: 'Ce qui a mal tourné',
    type_wrong_hint: 'Signaler une réaction',
    severity: 'Sévérité',
    sev_mild: 'Légère',
    sev_mild_hint: 'Démangeaisons, gêne légère',
    sev_moderate: 'Modérée',
    sev_moderate_hint: 'Urticaire, maux d\'estomac',
    sev_severe: 'Sévère',
    sev_severe_hint: 'Gonflement, respiration, vomissements',
    sev_life: 'Potentiellement mortelle',
    sev_life_hint: 'Anaphylaxie, urgences',
    share_dialog_eyebrow: 'PARTAGER UN SIGNAL',
    share_dialog_title_positive: "Qu'est-ce qui a marché pour vous ?",
    share_dialog_title_negative: 'Signaler une réaction',
    field_restaurant: 'Restaurant',
    restaurant_placeholder: 'Choisir un restaurant…',
    field_dish: 'Nom du plat',
    dish_placeholder: 'ex. Pappardelle sans gluten',
    field_restrictions: 'Restrictions respectées',
    what_worked_label: 'Ce qui a marché',
    what_happened_label: "Ce qui s'est passé",
    what_worked_placeholder: 'La cuisine a préparé une station dédiée sans gluten avec huile d\'olive et légumes frais…',
    what_happened_placeholder: "J'ai dit au serveur que j'étais allergique aux produits laitiers, mais le plat est arrivé avec une sauce crémeuse. La cuisine ne l'a pas remarqué.",
    add_photo: 'Ajouter une photo (facultatif)',
    notes_label: 'Notes supplémentaires (facultatif)',
    notes_placeholder: 'Le personnel connaissait très bien la contamination croisée…',
    confidence: 'Confiance',
    evidence: 'Preuve',
    anonymous: 'Partager anonymement',
    cancel: 'Annuler',
    submit: 'Partager le signal',
    err_pick_restaurant: 'Choisissez un restaurant.',
    err_name_dish: 'Nommez le plat.',
    err_pick_restriction: 'Choisissez au moins une restriction.',
    err_describe_worked: 'Décrivez ce qui a marché.',
    err_describe_happened: 'Décrivez ce qui s\'est passé.',
    err_pick_severity: 'Choisissez un niveau de sévérité.',
    err_share_failed: 'Échec du partage du signal.',
    share_success: 'Signal partagé — merci.',
    photo_drop_hint: 'Cliquez ou déposez une photo du plat, du menu ou du reçu',
    photo_drop_active: 'Déposez la photo ici',
    photo_size_note: 'JPG, PNG, WebP, HEIC ou GIF — jusqu\'à 10 Mo',
    photo_replace: 'Remplacer',
    photo_remove: 'Supprimer',
    photo_not_image: "N'est pas une image",
    photo_too_large: 'Trop volumineux (10 Mo max)',
    reaction_report: 'Rapport de réaction',
    card_by: 'par {name}',
    card_anon: 'partagé anonymement',
    evidence_menu_label: 'Étiquette du menu',
    evidence_server_confirmed: 'Confirmé par le serveur',
    evidence_kitchen_confirmed: 'Confirmé par la cuisine',
    evidence_user_experience: 'Expérience utilisateur',
    ver_unverified: 'Non vérifié',
    ver_restaurant: 'Vérifié par le restaurant',
    ver_staff: 'Vérifié par le personnel',
    ver_kitchen: 'Confirmé par la cuisine',
  },
  landing: {
    eyebrow: 'Découverte par IA',
    headline_l1: 'Ne stressez plus',
    headline_l2: 'pour savoir',
    headline_emphasis: 'quoi manger',
    sub: "Discutez avec le menu de n'importe quel restaurant. Recevez des recommandations adaptées à vos allergies, votre régime et votre humeur. Nous apprendrons ce qui vous convient.",
    pick_eyebrow: 'Choisissez ce qui compte pour vous',
    pick_tag_in: 'Choisissez une étiquette {category}',
    cta_primary: 'Commencer à découvrir',
    cta_secondary: 'Voir la démo',
    preview_example: 'Aperçu d\'exemple',
    stat_restaurants: 'Restaurants',
    group_religious:              'Religieuses',
    group_diet:                   'Régime',
    group_allergies:              'Allergies',
    group_therapeutic:            'Thérapeutiques',
    group_lifestyle:              'Mode de vie',
    group_ethical:                'Éthiques',
    group_life_stage:             'Étape de vie',
    group_glp1_portions:          'GLP-1 / Portions',
    group_sensory_neurodivergent: 'Sensoriel / Neurodivergent',
  },
  onboarding: {
    step_of: 'ÉTAPE {step} SUR 3',
    skip: 'Passer pour l\'instant',
    back: 'Retour',
    continue: 'Continuer',
    finish: 'Terminer et aller au tableau de bord',
    error_save: 'Erreur de sauvegarde. Réessayez.',
    s1_eyebrow: 'LA SÉCURITÉ D\'ABORD',
    s1_title: 'Qu\'avez-vous besoin d\'éviter ?',
    s1_body: 'Le chat utilise vos allergies sur chaque menu pour signaler les plats à risque. Cochez celles qui vous concernent et ajoutez leur sévérité.',
    s1_common_eyebrow: 'ALLERGÈNES COURANTS',
    s1_custom_placeholder: 'Autre (lupin, céleri, mangue)…',
    s1_add: 'Ajouter',
    s1_your_list: 'VOS ALLERGIES ({count}) — DÉFINIR LA SÉVÉRITÉ',
    s2_eyebrow: 'VOS GOÛTS',
    s2_title: 'Qu\'aimez-vous manger ?',
    s2_body: "Pas de mauvaises réponses. Choisissez un style alimentaire si vous en avez un, et vos cuisines préférées — le chat classera les recommandations autour de cela.",
    s2_diet_eyebrow: 'STYLE ALIMENTAIRE',
    s2_cuisines_eyebrow: 'CUISINES PRÉFÉRÉES',
    s3_eyebrow: 'COMMENT VOUS DÎNEZ',
    s3_title: 'Pour qui on cuisine tout ça ?',
    s3_body: 'Un rapide aperçu pour que le chat propose la bonne taille, le bon prix et la bonne ambiance.',
    s3_who_eyebrow: 'VOUS DÎNEZ PRINCIPALEMENT',
    s3_how_often_eyebrow: 'FRÉQUENCE AU RESTAURANT',
    s3_ambition_eyebrow: 'VOTRE HUMEUR CULINAIRE',
  },
  about: {
    eyebrow: 'À PROPOS',
    headline_l1: 'Sortir manger ne devrait pas',
    headline_emphasis: 'avoir besoin d\'un avertissement',
    sub: 'Chat With Menu existe pour toute personne qui s\'est déjà excusée de son allergie, a plissé les yeux sur une liste d\'ingrédients à table, ou a mangé une salade ennuyeuse parce que "sûr" était la seule option.',
    story_eyebrow: 'L\'HISTOIRE',
    story_title: 'Tout a commencé par un mauvais dîner.',
    story_p1: "Une famille de quatre. Un nouveau restaurant. Un enfant allergique aux arachides. Un serveur qui n'était pas sûr. Un manager qui a deviné. Un passage à l'hôpital qui s'est heureusement bien terminé, mais qui n'aurait pas dû arriver.",
    story_p2: "Le problème n'était pas que le restaurant s'en fichait — c'est que l'information dont chaque partie avait besoin (les allergies du client, les ingrédients du plat, la manipulation en cuisine) ne vivait pas au même endroit. Nous avons construit Chat With Menu pour que ce soit le cas.",
    values_eyebrow: 'POUR QUOI NOUS CONSTRUISONS',
    values_title: 'Nos principes',
    v1_title: 'La sécurité avant tout',
    v1_body: "Une mauvaise réponse sur une allergie n'est jamais acceptable. Nous construisons pour ce cas sur mille, parce que pour ce client-là, c'est le seul cas qui compte.",
    v2_title: 'La confiance se mérite',
    v2_body: 'Chaque étiquette d\'allergène, chaque recommandation, chaque score de confiance — appuyés par les données réelles du restaurant, pas des devinettes.',
    v3_title: 'Signaux communautaires',
    v3_body: "Les clients partagent ce qui a marché, les restaurants ce qu'ils accommodent. La table devient plus intelligente à chaque dîner.",
    v4_title: 'Dîner devrait faire du bien',
    v4_body: "Toute personne avec une restriction connaît cette boule au ventre dans un nouveau restaurant. On est là pour la transformer en plaisir.",
    timeline_eyebrow: 'CHRONOLOGIE',
    timeline_title: 'Par où nous sommes passés',
    ms1_label: 'Idée',
    ms1_body:  "Fondé après une mauvaise soirée avec un enfant allergique aux arachides dans une ville pleine de menus non étiquetés.",
    ms2_label: 'Lancement',
    ms2_body:  'Lancement de Chat With Menu : interrogez n\'importe quel menu sur vos allergies et votre régime.',
    ms3_label: 'Échelle',
    ms3_body:  '{count} restaurants sur Chat With Menu, et ce n\'est qu\'un début.',
    cta_headline_l1: 'Mangez en',
    cta_headline_emphasis: 'confiance',
    cta_body: 'Rejoignez les clients qui utilisent Chat With Menu pour découvrir des restaurants qui prennent leur table au sérieux.',
    cta_primary: 'Commencer gratuitement',
    cta_secondary: 'Voir comment ça marche',
  },
  how: {
    eyebrow: 'COMMENT ÇA MARCHE',
    headline_l1: 'Dîner avec des allergies,',
    headline_emphasis: 'simplifié',
    sub: 'Trois étapes pour les clients. Trois étapes pour les restaurants. Tout le monde mange en confiance.',
    track_diner: 'Pour les clients',
    track_restaurant: 'Pour les restaurants',
    d1_title: 'Parlez-nous de votre table',
    d1_body: 'Ajoutez allergies, préférences et avec qui vous dînez. Votre profil voyage avec vous — plus besoin de tout répéter à chaque restaurant.',
    d2_title: 'Discutez avec n\'importe quel menu',
    d2_body: "Demandez \"qu'est-ce qui est sûr pour l'allergie aux arachides de ma fille ?\" et recevez des choix classés en quelques secondes. Basé sur les vrais ingrédients du restaurant — pas des devinettes.",
    d3_title: 'Commandez en confiance',
    d3_body: "Enregistrez ce qui a marché, partagez les choix sûrs avec votre table, et recommandez sans repartir de zéro la prochaine fois.",
    r1_title: 'Déposez votre menu',
    r1_body: "Collez un menu ou téléchargez vos documents. Notre IA étiquette automatiquement allergènes et attributs alimentaires — vous vérifiez et publiez.",
    r2_title: 'Votre équipe a l\'air experte',
    r2_body: 'Les serveurs consultent les allergies sur n\'importe quel plat en quelques secondes. Zéro formation. Chaque question client répondue correctement.',
    r3_title: 'Les clients vous trouvent',
    r3_body: 'Les clients soucieux des allergies utilisent Chat With Menu pour trouver des restaurants qui prennent leurs besoins au sérieux. Le bouche-à-oreille fait le reste.',
    stat3_label: 'restaurants sur la plateforme',
    cta_title: 'Prêt à manger en confiance ?',
    cta_primary: 'Commencer gratuitement',
    cta_secondary: 'Parcourir les restaurants',
  },
  toasts: {
    profile_saved: 'Profil enregistré.',
    link_copied: 'Lien copié.',
    menu_saved: 'Menu enregistré.',
    review_submitted: 'Avis envoyé. Merci !',
  },
};

// Portuguese
const pt = {
  common: {
    save: 'Salvar', cancel: 'Cancelar', close: 'Fechar', back: 'Voltar', continue: 'Continuar',
    yes: 'Sim', no: 'Não', loading: 'Carregando…', saving: 'Salvando…', saved: 'Salvo.',
    edit: 'Editar', delete: 'Excluir', add: 'Adicionar', remove: 'Remover', done: 'Pronto',
    search: 'Buscar', filter: 'Filtrar', clear: 'Limpar', apply: 'Aplicar',
    next: 'Próximo', previous: 'Anterior', skip: 'Pular', submit: 'Enviar',
    confirm: 'Confirmar', required: 'Obrigatório', optional: 'Opcional',
    error: 'Algo deu errado.', retry: 'Tentar de novo',
  },
  nav: {
    discover: 'Descobrir',
    for_restaurants: 'Para Empresas',
    pricing: 'Preços',
    dashboard: 'Painel',
    notifications: 'Notificações',
    sign_in: 'Entrar',
    sign_out: 'Sair',
    get_started: 'Começar',
    home_aria: 'Início',
  },
  footer: {
    how_it_works: 'Como funciona',
    about: 'Sobre',
    billing: 'Faturamento',
    settings: 'Ajustes',
    terms: 'Termos',
    privacy: 'Privacidade',
    contact: 'Contato',
    copyright: '© {year} Chat With Menu',
  },
  auth: {
    sign_in_title: 'Bem-vindo de volta',
    sign_up_title: 'Crie sua conta',
    sign_in_sub: 'Entre pra continuar de onde você parou.',
    sign_up_sub: 'Salve restaurantes, chatee com menus, ganhe pontos.',
    tab_sign_in: 'Entrar',
    tab_sign_up: 'Criar conta',
    full_name: 'Nome completo',
    name_placeholder: 'Seu nome',
    email: 'E-mail',
    email_placeholder: 'voce@exemplo.com',
    password: 'Senha',
    password_placeholder: 'No mínimo 8 caracteres',
    show: 'Mostrar',
    hide: 'Ocultar',
    show_password: 'Mostrar senha',
    hide_password: 'Ocultar senha',
    continue_with_google: 'Continuar com Google',
    continue_with_apple: 'Continuar com Apple',
    or_email: 'ou continuar com e-mail',
    no_account: 'Não tem conta?',
    have_account: 'Já tem conta?',
    forgot: 'Esqueceu a senha?',
    submit_sign_in: 'Entrar',
    submit_sign_up: 'Criar conta',
    submitting_sign_in: 'Entrando…',
    submitting_sign_up: 'Criando conta…',
    err_invalid: 'Credenciais inválidas. Tente de novo.',
    err_network: 'Sem conexão com o servidor. Verifique a internet e tente de novo.',
    err_oauth_soon: 'O login com Google e Apple chega logo. Por enquanto, continue com e-mail.',
  },
  settings: {
    eyebrow: 'AJUSTES',
    title_l1: 'Ajuste como',
    title_l2: 'te alcançamos',
    subtitle: 'Notificações, privacidade, idioma. Para perfil alimentar, use Painel → Alergias.',
    channels_eyebrow: 'COMO TE ALCANÇAMOS',
    channels_title: 'Canais de notificação',
    push: 'Push',
    push_hint: 'Neste dispositivo e no app.',
    email: 'E-mail',
    email_hint: 'Recibos de faturamento chegam de qualquer forma.',
    sms: 'SMS',
    sms_hint: 'Apenas alertas urgentes. Tarifas padrão.',
    events_eyebrow: 'TIPOS DE EVENTO',
    events_title: 'Sobre o que me notificar',
    cat_table_invites: 'Convites de mesa',
    cat_table_invites_hint: 'Alguém pede pra se juntar à sua mesa, ou aceita seu convite',
    cat_safety_alerts: 'Alertas de segurança',
    cat_safety_alerts_hint: 'Reações, recalls ou novos riscos de alérgenos nos lugares que você segue',
    cat_dish_matches: 'Pratos pra você',
    cat_dish_matches_hint: 'Novos pratos seguros que combinam com seu perfil',
    cat_order_updates: 'Atualizações do pedido',
    cat_order_updates_hint: 'Aceito, pronto pra retirada, entregue',
    cat_reviews_on_me: 'Respostas às suas avaliações',
    cat_reviews_on_me_hint: 'Restaurantes respondem a algo que você postou',
    cat_billing: 'Faturamento',
    cat_billing_hint: 'Faturas, mudanças de plano, pagamentos falhos',
    cat_product_updates: 'Novidades do produto',
    cat_product_updates_hint: 'Novos recursos (pouco frequente)',
    cat_weekly_digest: 'Resumo semanal',
    cat_weekly_digest_hint: 'Domingo: novos lugares + notas da sua mesa',
    quiet_eyebrow: 'NÃO ME AVISE',
    quiet_title: 'Horário silencioso',
    quiet_toggle: 'Silenciar avisos não críticos à noite',
    quiet_hint: 'Alertas de segurança sempre passam.',
    quiet_from: 'DE',
    quiet_to: 'ATÉ',
    privacy_eyebrow: 'O QUE OUTROS VEEM',
    privacy_title: 'Privacidade',
    priv_share_prefs: 'Compartilhar meu perfil com restaurantes no chat',
    priv_share_prefs_hint: 'Garçons veem suas restrições antes.',
    priv_attribute_signals: 'Assinar minhas señais publicamente',
    priv_attribute_signals_hint: 'Desativado = sinais anônimos.',
    priv_show_review_name: 'Mostrar meu nome nas avaliações',
    priv_show_review_name_hint: "Desativado = 'Cliente anônimo'.",
    priv_allow_invites: 'Permitir convites de amigos',
    priv_allow_invites_hint: 'Desativado = só os que você inicia.',
    locale_eyebrow: 'REGIÃO',
    locale_title: 'Idioma e moeda',
    language: 'Idioma',
    currency: 'Moeda',
    measurements: 'Medidas',
    imperial: 'Imperial (fl oz, °F)',
    metric: 'Métrico (ml, °C)',
    danger_eyebrow: 'ZONA DE PERIGO',
    danger_title: 'Ações da conta',
    reset: 'Resetar ajustes',
    reset_hint: 'Notificações, privacidade, região. Não mexe em perfil alimentar nem locais salvos.',
    reset_action: 'Resetar',
    download: 'Baixar meus dados',
    download_hint: 'Preferências, avaliações, locais salvos — em JSON. Chega por e-mail em 24 h.',
    download_action: 'Solicitar',
    delete_account: 'Excluir conta',
    delete_account_hint: 'Apaga seu perfil, avaliações e sinais. Não reverte.',
    delete_action: 'Excluir',
  },
  chat: {
    empty_title: 'Converse com o menu',
    empty_subtitle: 'Pergunte sobre alérgenos, combinações, ou o que a cozinha customiza.',
    input_placeholder: 'Pergunte um alérgeno, um prato, o clima…',
    send: 'Enviar',
    send_failed: 'Falha ao enviar. Tente de novo.',
    ai_offline: 'O assistente tá recuperando o fôlego. Tente em alguns segundos.',
    reset_chat: 'Nova conversa',
    suggested_prompts_label: 'Experimente',
    suggest_allergens: 'O que tem sem glúten e sem lactose neste menu?',
    suggest_recommend: 'Do que a cozinha mais se orgulha hoje à noite?',
    suggest_pair: 'O que combina com o salmão?',
    suggest_cross: 'Vocês têm estação sem amendoim?',
  },
  discover: {
    hero_title: 'Restaurantes que seu corpo vai agradecer',
    hero_subtitle: 'Arraste pra direita pra salvar, esquerda pra pular — a gente aprende o que funciona pra você.',
    brand_tagline: 'Descubrir restaurantes',
    auto_banner_title: 'Filtrado do seu perfil',
    auto_banner_body: ' — mostrando lugares que combinam com {tags}.',
    clear_auto: 'Mostrar tudo',
    result_count: '{shown} de {total} lugares',
    empty: 'Nada combina. Tente tirar uma etiqueta.',
  },
  dashboard: {
    welcome: 'Bem-vindo, {name}',
    welcome_fallback: 'Bem-vindo…',
    subtitle_diner:    'Navegue entre descoberta, família e ferramentas de mesa sem se perder.',
    subtitle_merchant: 'Gerencie seus restaurantes, menus e sinais dos clientes.',
    subtitle_server:   'Consultas rápidas de alergias e especiais durante o atendimento.',
    role_diner:   'Cliente',
    role_admin:   'Admin',
    role_owner:   'Proprietário',
    role_server:  'Garçom',
    tab_allergies:   'Alergias',
    tab_preferences: 'Preferências',
    tab_profile:     'Editar perfil',
    tab_family:      'Família',
    tab_table:       'Minha mesa',
    tab_history:     'Histórico',
    tab_rewards:     'Recompensas',
    tab_discover:    'Descobrir',
    tab_operations:  'Operações',
    tab_menus:       'Gerenciar menus',
    tab_add_rest:    'Adicionar restaurante',
    tab_service:     'Ferramentas',
  },
  dish: {
    back: 'Voltar',
    menu_fallback: 'MENU',
    needs_review: 'Precisa revisão',
    ask_about_this: 'Perguntar sobre isso',
    next_step_eyebrow: 'PRÓXIMO PASSO',
    next_step_title: 'Não tem certeza se esse prato é seguro?',
    next_step_body: 'Chatee com o menu de {name} — vamos cruzar suas alergias num toque.',
    share_safety_cta: 'Compartilhar sinal',
    chat_about: 'Chatear sobre {name}',
    allergen_eyebrow: 'DETALHES DE ALÉRGENOS',
    allergen_clear_title: 'Sem alérgenos marcados',
    allergen_clear_body: 'Sempre confirme com o garçom se tiver alergia severa.',
    allergen_count: '{count} alérgenos a observar',
    allergen_count_one: '1 alérgeno a observar',
    allergen_hint: 'Severidade inferida de etiquetas, métodos de preparo e ingredientes removíveis.',
    sev_contains: 'Contém',
    sev_prepared: 'Preparado com',
    sev_may_contain: 'Pode conter',
    sev_critical: 'Crítico',
    prep_eyebrow: 'PREPARO',
    prep_title: 'Como é feito',
    prep_empty: 'Sem detalhes ainda. Pergunte à cozinha — eles sabem.',
    prep_methods_eyebrow: 'MÉTODOS',
    prep_removable_eyebrow: 'PODE SER REMOVIDO',
    prep_mods_eyebrow: 'PERSONALIZAÇÕES',
    prep_required: 'obrigatório',
    pair_eyebrow: 'COMBINA COM',
    pair_title: 'Monte uma refeição completa',
    pair_empty: 'Vamos sugerir combinações quando o menu tiver mais pratos.',
    pair_drink: 'Para beber',
    pair_start: 'Para começar',
    pair_finish: 'Para finalizar',
    pair_side: 'Acompanhamento',
    similar_eyebrow_prefix: 'MAIS DE',
    similar_menu_fallback: 'ESTE MENU',
    similar_title: 'Pratos similares',
    similar_no_allergens: 'Sem alérgenos marcados',
    similar_n_allergens: '{n} alérgenos',
    similar_one_allergen: '1 alérgeno',
    loading: 'Carregando prato…',
    not_found_title: 'Prato não encontrado',
    not_found_body: 'Pode ter sido removido do menu, ou o link está incorreto.',
    not_found_cta: 'Voltar',
  },
  restaurant: {
    write_review: 'Escrever avaliação',
    chat_cta: 'Chatear com o menu',
    safety_signal_cta: 'Compartilhar sinal',
    ask_table_cta: 'Perguntar à mesa',
    dietary_eyebrow: 'O QUE ATENDEM',
    menu_highlights: 'DESTAQUES DO MENU',
    contact_eyebrow: 'CONTATO',
    contact_none: 'Sem informações de contato publicadas.',
    safety_eyebrow: 'SINAIS DE SEGURANÇA',
    safety_body: 'Pratos seguros confirmados pela comunidade. Compartilhe o seu.',
    view_signals: 'Ver sinais',
    see_full_menu: 'Ver menu completo no chat',
    no_menu: 'Nenhum menu publicado. Chatee com o restaurante pra ver opções.',
    reviews_title: 'O que os clientes dizem',
    reviews_eyebrow: 'AVALIAÇÕES RECENTES',
    reviews_count: '{count} avaliações',
    reviews_safety_eyebrow: 'SEGURANÇA ALÉRGICA',
    reviews_safety_hint: 'Quanta confiança os clientes sentiram ao perguntar sobre alérgenos.',
    reviews_see_all: 'Ver todas {count} avaliações',
    loading: 'Carregando restaurante…',
    not_found_title: 'Restaurante não encontrado',
    not_found_body: 'Pode ter sido removido, ou o link está incorreto.',
    not_found_cta: 'Ver restaurantes',
    time_today: 'hoje',
    time_yesterday: 'ontem',
    time_days_ago: 'há {n} dias',
    time_week_ago: 'há 1 semana',
    time_weeks_ago: 'há {n} semanas',
    time_month_ago: 'há 1 mês',
    time_months_ago: 'há {n} meses',
  },
  menu_manager: {
    eyebrow: 'GESTOR DE MENU',
    title_l1: 'Seu menu,',
    title_l2: 'limpo e atual',
    subtitle: 'Navegue e edite pratos, publique especiais, ajuste dicas e mantenha seu perfil afiado.',
    tab_items: 'Pratos',
    tab_specials: 'Especiais',
    tab_upsell: 'Dicas',
    tab_profile: 'Perfil',
    publish: 'Publicar menu',
    publish_n: 'Publicar ({n})',
    publish_first: 'Publique seu menu',
    discard: 'Descartar rascunho',
    status_live: 'No ar',
    status_unpublished: '{n} não publicados',
    status_not_published: 'Não publicado',
    saved_toast: 'Menu publicado — os clientes veem agora.',
    reverted_toast: 'Voltou para a versão ao vivo.',
  },
  safety: {
    eyebrow: 'SINAIS DE SEGURANÇA',
    title_l1: 'Saiba o que é',
    title_l2: 'realmente seguro',
    subtitle: 'Relatos da comunidade, classificados por quem confirmou — garçom, cozinha ou experiência vivida.',
    share_cta: 'Compartilhar um sinal',
    type_worked: 'Funcionou',
    type_worked_hint: 'Prato seguro pra recomendar',
    type_wrong: 'Deu errado',
    type_wrong_hint: 'Relatar uma reação',
    severity: 'Severidade',
    sev_mild: 'Leve',
    sev_moderate: 'Moderada',
    sev_severe: 'Severa',
    sev_life: 'Risco de vida',
    filter_all: 'Tudo',
    filter_worked: '✓ Funcionou',
    filter_reactions: '⚠ Reações',
    what_worked_label: 'O que funcionou',
    what_happened_label: 'O que aconteceu',
    add_photo: 'Adicionar foto (opcional)',
  },
  landing: {
    eyebrow: 'Descoberta com IA',
    headline_l1: 'Nunca se preocupe',
    headline_l2: 'sobre',
    headline_emphasis: 'o que comer',
    sub: 'Converse com o menu de qualquer restaurante. Receba recomendações sob medida para suas alergias, dieta e humor. Vamos aprender o que funciona para você.',
    pick_eyebrow: 'Escolha o que importa para você',
    pick_tag_in: 'Escolha uma etiqueta de {category}',
    cta_primary: 'Começar a descobrir',
    cta_secondary: 'Ver demo',
    preview_example: 'Prévia de exemplo',
    stat_restaurants: 'Restaurantes',
    group_religious:              'Religiosas',
    group_diet:                   'Dieta',
    group_allergies:              'Alergias',
    group_therapeutic:            'Terapêuticas',
    group_lifestyle:              'Estilo de vida',
    group_ethical:                'Éticas',
    group_life_stage:             'Fase da vida',
    group_glp1_portions:          'GLP-1 / Porções',
    group_sensory_neurodivergent: 'Sensorial / Neurodivergente',
  },
  onboarding: {
    step_of: 'PASSO {step} DE 3',
    skip: 'Pular por agora',
    back: 'Voltar',
    continue: 'Continuar',
    finish: 'Finalizar e ir ao painel',
    error_save: 'Algo deu errado ao salvar. Tente de novo.',
    s1_eyebrow: 'SEGURANÇA PRIMEIRO',
    s1_title: 'O que você precisa evitar?',
    s1_body: 'O chat usa suas alergias em cada menu para sinalizar pratos perigosos. Toque nas que se aplicam e defina a severidade.',
    s1_common_eyebrow: 'ALÉRGENOS COMUNS',
    s1_custom_placeholder: 'Outro (ex. tremoço, aipo, manga)…',
    s1_add: 'Adicionar',
    s1_your_list: 'SUAS ALERGIAS ({count}) — DEFINA SEVERIDADE',
    s2_eyebrow: 'SEU PALADAR',
    s2_title: 'O que você adora comer?',
    s2_body: 'Sem respostas erradas. Escolha um estilo alimentar se tiver, e suas culinárias favoritas — o chat vai ranquear recomendações em torno disso.',
    s2_diet_eyebrow: 'ESTILO ALIMENTAR',
    s2_cuisines_eyebrow: 'CULINÁRIAS FAVORITAS',
    s3_eyebrow: 'COMO VOCÊ JANTA',
    s3_title: 'Pra quem a gente tá cozinhando?',
    s3_body: 'Um retrato rápido pro chat sugerir o tamanho, preço e clima certos.',
    s3_who_eyebrow: 'COM QUEM VOCÊ JANTA',
    s3_how_often_eyebrow: 'COM QUE FREQUÊNCIA COME FORA',
    s3_ambition_eyebrow: 'SEU HUMOR CULINÁRIO',
  },
  about: {
    eyebrow: 'SOBRE',
    headline_l1: 'Comer fora não deveria vir',
    headline_emphasis: 'com um aviso',
    sub: 'Chat With Menu existe para cada pessoa que já pediu desculpa pela alergia, apertou os olhos lendo ingredientes à mesa, ou comeu uma salada sem graça porque "seguro" era a única opção.',
    story_eyebrow: 'A HISTÓRIA',
    story_title: 'Começamos com um jantar ruim.',
    story_p1: 'Uma família de quatro. Um restaurante novo. Uma criança alérgica a amendoim. Um garçom sem certeza. Um gerente que chutou. Uma ida ao hospital que (felizmente) acabou bem, mas não precisava ter acontecido.',
    story_p2: 'O problema não era que o restaurante não se importava — é que a informação que cada parte precisava (alergias do cliente, ingredientes do prato, manuseio na cozinha) não vivia no mesmo lugar. Criamos Chat With Menu para que passe a viver.',
    values_eyebrow: 'POR QUE CONSTRUÍMOS',
    values_title: 'Nossos princípios',
    v1_title: 'Segurança primeiro, sempre',
    v1_body: 'Uma resposta errada sobre alergia nunca é aceitável. Construímos para aquele caso um-em-mil porque para esse cliente, é o único caso.',
    v2_title: 'Confiança se conquista',
    v2_body: 'Cada etiqueta de alérgeno, cada recomendação, cada pontuação de confiança — baseados nos dados reais do restaurante, não em chutes.',
    v3_title: 'Sinais da comunidade',
    v3_body: 'Clientes compartilham o que funcionou, restaurantes o que acomodam. A mesa fica mais inteligente a cada jantar.',
    v4_title: 'Jantar deve fazer bem',
    v4_body: 'Quem tem restrição conhece aquele nó no estômago num restaurante novo. Estamos aqui para transformar isso em alegria.',
    timeline_eyebrow: 'LINHA DO TEMPO',
    timeline_title: 'Por onde passamos',
    ms1_label: 'Ideia',
    ms1_body:  'Fundado depois de uma noite ruim com uma criança alérgica a amendoim numa cidade cheia de menus sem etiqueta.',
    ms2_label: 'Lançamento',
    ms2_body:  'Lançamento do Chat With Menu: pergunte a qualquer menu sobre suas alergias e sua dieta.',
    ms3_label: 'Escala',
    ms3_body:  '{count} restaurantes no Chat With Menu, e contando.',
    cta_headline_l1: 'Coma com',
    cta_headline_emphasis: 'confiança',
    cta_body: 'Junte-se aos clientes que usam Chat With Menu para encontrar restaurantes que levam a mesa a sério.',
    cta_primary: 'Começar grátis',
    cta_secondary: 'Ver como funciona',
  },
  how: {
    eyebrow: 'COMO FUNCIONA',
    headline_l1: 'Jantar com alergias,',
    headline_emphasis: 'simplificado',
    sub: 'Três passos para clientes. Três passos para restaurantes. Todos comem com confiança.',
    track_diner: 'Para clientes',
    track_restaurant: 'Para restaurantes',
    d1_title: 'Conte sobre sua mesa',
    d1_body: 'Adicione alergias, preferências e com quem janta. Seu perfil viaja com você — sem repetir em cada restaurante.',
    d2_title: 'Converse com qualquer menu',
    d2_body: 'Pergunte "o que é seguro para a alergia de amendoim da minha filha?" e receba opções em segundos. Baseado nos ingredientes reais — não em chutes.',
    d3_title: 'Peça com confiança',
    d3_body: 'Salve o que funcionou, compartilhe opções seguras com a mesa e peça de novo sem recomeçar.',
    r1_title: 'Envie seu menu',
    r1_body: 'Cole um menu ou suba documentos. Nossa IA etiqueta alérgenos e atributos automaticamente — você revisa e publica.',
    r2_title: 'Sua equipe parece expert',
    r2_body: 'Garçons consultam alergias em qualquer prato em segundos. Zero treinamento. Toda dúvida do cliente respondida corretamente.',
    r3_title: 'Clientes encontram você',
    r3_body: 'Clientes atentos a alergias usam Chat With Menu para achar restaurantes que levam suas necessidades a sério. O boca a boca se espalha.',
    stat3_label: 'restaurantes na plataforma',
    cta_title: 'Pronto para comer com confiança?',
    cta_primary: 'Começar grátis',
    cta_secondary: 'Ver restaurantes',
  },
  toasts: {
    profile_saved: 'Perfil salvo.',
    link_copied: 'Link copiado.',
    menu_saved: 'Menu salvo.',
    review_submitted: 'Avaliação enviada. Obrigado!',
  },
};

// Italian (subset — key strings only, English fallback for the rest)
const it = {
  common: {
    save: 'Salva', cancel: 'Annulla', close: 'Chiudi', back: 'Indietro', continue: 'Continua',
    yes: 'Sì', no: 'No', loading: 'Caricamento…', saving: 'Salvataggio…', saved: 'Salvato.',
    edit: 'Modifica', delete: 'Elimina', add: 'Aggiungi', remove: 'Rimuovi', done: 'Fatto',
    search: 'Cerca', filter: 'Filtra', clear: 'Pulisci', apply: 'Applica',
    next: 'Avanti', previous: 'Indietro', skip: 'Salta', submit: 'Invia',
    confirm: 'Conferma', required: 'Obbligatorio', optional: 'Opzionale',
    error: 'Qualcosa è andato storto.', retry: 'Riprova',
  },
  nav: {
    discover: 'Scopri',
    for_restaurants: 'Per le aziende',
    pricing: 'Prezzi',
    dashboard: 'Pannello',
    notifications: 'Notifiche',
    sign_in: 'Accedi',
    sign_out: 'Esci',
    get_started: 'Inizia',
    home_aria: 'Home',
  },
  footer: {
    how_it_works: 'Come funziona',
    about: 'Chi siamo',
    billing: 'Fatturazione',
    settings: 'Impostazioni',
    terms: 'Termini',
    privacy: 'Privacy',
    contact: 'Contatti',
    copyright: '© {year} Chat With Menu',
  },
  chat: {
    empty_title: 'Chatta col menu',
    empty_subtitle: 'Chiedi di allergeni, abbinamenti, o cosa può personalizzare la cucina.',
    input_placeholder: 'Chiedi un allergene, un piatto, l\'atmosfera…',
    send: 'Invia',
    send_failed: 'Invio fallito. Riprova.',
    ai_offline: "L'assistente sta riprendendo fiato. Riprova tra poco.",
    reset_chat: 'Nuova conversazione',
    suggested_prompts_label: 'Prova a chiedere',
    suggest_allergens: 'Cosa è senza glutine e senza lattosio in questo menu?',
    suggest_recommend: 'Di cosa è più orgogliosa la cucina stasera?',
    suggest_pair: 'Cosa si abbina bene al salmone?',
    suggest_cross: 'Avete una postazione senza arachidi?',
  },
  discover: {
    hero_title: 'Ristoranti che il tuo corpo ringrazierà',
    hero_subtitle: 'Scorri a destra per salvare, sinistra per saltare.',
    brand_tagline: 'Scoperta ristoranti',
    auto_banner_title: 'Filtrato dal tuo profilo',
    auto_banner_body: ' — posti che combaciano con {tags}.',
    clear_auto: 'Mostra tutto',
  },
  settings: {
    eyebrow: 'IMPOSTAZIONI',
    title_l1: 'Regola come',
    title_l2: 'ti contattiamo',
    language: 'Lingua',
    currency: 'Valuta',
  },
  safety: {
    title_l1: 'Sapere cosa è',
    title_l2: 'davvero sicuro',
    share_cta: 'Condividi un segnale',
    type_worked: 'Ha funzionato',
    type_wrong: 'Andata male',
    sev_mild: 'Lieve',
    sev_moderate: 'Moderata',
    sev_severe: 'Severa',
    sev_life: 'Potenzialmente letale',
  },
  menu_manager: {
    title_l1: 'Il tuo menu,',
    title_l2: 'pulito e aggiornato',
    tab_items: 'Piatti',
    tab_specials: 'Speciali',
    tab_upsell: 'Consigli',
    tab_profile: 'Profilo',
    publish: 'Pubblica menu',
    status_live: 'In diretta',
    status_not_published: 'Non pubblicato',
  },
  dish: {
    back: 'Indietro',
    menu_fallback: 'MENU',
    needs_review: 'Da rivedere',
    ask_about_this: 'Chiedi qualcosa',
    next_step_eyebrow: 'PROSSIMO PASSO',
    next_step_title: 'Non sei sicuro che questo piatto sia sicuro?',
    next_step_body: 'Chatta col menu di {name} — incrociamo le tue allergie in un tocco.',
    share_safety_cta: 'Condividi un segnale',
    chat_about: 'Chatta su {name}',
    allergen_eyebrow: 'DETTAGLI ALLERGENI',
    allergen_clear_title: 'Nessun allergene segnalato',
    allergen_clear_body: 'Conferma sempre col cameriere se hai un\'allergia severa.',
    allergen_count: '{count} allergeni da notare',
    allergen_count_one: '1 allergene da notare',
    allergen_hint: 'Severità dedotta dalle etichette, metodi di preparazione e ingredienti rimovibili.',
    sev_contains: 'Contiene',
    sev_prepared: 'Preparato con',
    sev_may_contain: 'Può contenere',
    sev_critical: 'Critico',
    prep_eyebrow: 'PREPARAZIONE',
    prep_title: 'Come si prepara',
    prep_empty: 'Nessun dettaglio. Chiedi alla cucina — lo sapranno.',
    prep_methods_eyebrow: 'METODI',
    prep_removable_eyebrow: 'SI PUÒ RIMUOVERE',
    prep_mods_eyebrow: 'PERSONALIZZAZIONI',
    prep_required: 'richiesto',
    pair_eyebrow: 'SI ABBINA A',
    pair_title: 'Costruisci un pasto completo',
    pair_empty: 'Suggeriremo abbinamenti quando il menu avrà più piatti.',
    pair_drink: 'Da bere',
    pair_start: 'Per iniziare',
    pair_finish: 'Per finire',
    pair_side: 'Contorno',
    similar_eyebrow_prefix: 'ALTRI DA',
    similar_menu_fallback: 'QUESTO MENU',
    similar_title: 'Piatti simili',
    similar_no_allergens: 'Nessun allergene segnalato',
    similar_n_allergens: '{n} allergeni',
    similar_one_allergen: '1 allergene',
    loading: 'Caricamento piatto…',
    not_found_title: 'Piatto non trovato',
    not_found_body: "L'articolo potrebbe essere stato rimosso dal menu, o il link è errato.",
    not_found_cta: 'Indietro',
  },
  restaurant: {
    write_review: 'Scrivi una recensione',
    chat_cta: 'Chatta col menu',
    safety_signal_cta: 'Condividi un segnale',
    ask_table_cta: 'Chiedi alla tavola',
    dietary_eyebrow: 'COSA ACCOLGONO',
    menu_highlights: 'IN EVIDENZA NEL MENU',
    contact_eyebrow: 'CONTATTO',
    contact_none: 'Nessun contatto pubblicato.',
    safety_eyebrow: 'SEGNALI DI SICUREZZA',
    safety_body: 'Piatti sicuri confermati dalla comunità. Condividi il tuo.',
    view_signals: 'Vedi i segnali',
    see_full_menu: 'Vedi tutto il menu in chat',
    no_menu: 'Nessun menu pubblicato. Chatta col ristorante per opzioni aggiornate.',
    reviews_title: 'Cosa dicono i clienti',
    reviews_eyebrow: 'RECENSIONI RECENTI',
    reviews_count: '{count} recensioni',
    reviews_safety_eyebrow: 'SICUREZZA ALLERGIE',
    reviews_safety_hint: 'Quanta fiducia hanno avuto i clienti nel parlare di allergeni.',
    reviews_see_all: 'Vedi tutte le {count} recensioni',
    loading: 'Caricamento ristorante…',
    not_found_title: 'Ristorante non trovato',
    not_found_body: 'Potrebbe essere stato rimosso o il link è errato.',
    not_found_cta: 'Sfoglia ristoranti',
    time_today: 'oggi',
    time_yesterday: 'ieri',
    time_days_ago: '{n} giorni fa',
    time_week_ago: '1 settimana fa',
    time_weeks_ago: '{n} settimane fa',
    time_month_ago: '1 mese fa',
    time_months_ago: '{n} mesi fa',
  },
  about: {
    eyebrow: 'CHI SIAMO',
    headline_l1: 'Mangiare fuori non dovrebbe',
    headline_emphasis: 'richiedere un avviso',
    sub: "Chat With Menu esiste per chiunque si sia scusato per un'allergia, abbia strizzato gli occhi su una lista d'ingredienti, o abbia mangiato un'insalata noiosa perché \"sicuro\" era l'unica opzione.",
    story_eyebrow: 'LA STORIA',
    story_title: 'Abbiamo iniziato con una cena andata male.',
    story_p1: "Una famiglia di quattro. Un nuovo ristorante. Un bambino allergico alle arachidi. Un cameriere incerto. Un manager che ha tirato a indovinare. Una corsa all'ospedale che (per fortuna) è finita bene, ma non doveva accadere.",
    story_p2: "Il problema non era che al ristorante non importasse — era che le informazioni necessarie (le allergie del cliente, gli ingredienti del piatto, la gestione in cucina) non vivevano nello stesso posto. Abbiamo costruito Chat With Menu perché lo facciano.",
    values_eyebrow: 'PER COSA COSTRUIAMO',
    values_title: 'I nostri principi',
    v1_title: 'Sicurezza prima di tutto',
    v1_body: "Una risposta sbagliata su un'allergia non è mai accettabile. Costruiamo per quel caso uno-su-mille perché per quel cliente, è l'unico caso.",
    v2_title: 'La fiducia si guadagna',
    v2_body: 'Ogni etichetta allergene, ogni consiglio, ogni punteggio di fiducia — basati sui dati reali del ristorante, non su ipotesi.',
    v3_title: 'Segnali dalla comunità',
    v3_body: 'I clienti condividono cosa ha funzionato, i ristoranti cosa accolgono. La tavola diventa più intelligente a ogni cena.',
    v4_title: 'Cenare deve far stare bene',
    v4_body: 'Chi ha una restrizione conosce quel nodo allo stomaco in un nuovo ristorante. Siamo qui per trasformarlo in piacere.',
    timeline_eyebrow: 'STORIA',
    timeline_title: 'Da dove veniamo',
    ms1_label: 'Idea',
    ms1_body:  'Fondato dopo una serata difficile con un bambino allergico alle arachidi in una città piena di menu non etichettati.',
    ms2_label: 'Lancio',
    ms2_body:  'Lancio di Chat With Menu: chiedi a qualsiasi menu delle tue allergie e della tua dieta.',
    ms3_label: 'Scala',
    ms3_body:  '{count} ristoranti su Chat With Menu, e il numero cresce.',
    cta_headline_l1: 'Mangia con',
    cta_headline_emphasis: 'fiducia',
    cta_body: 'Unisciti ai clienti che usano Chat With Menu per scoprire ristoranti che prendono sul serio la loro tavola.',
    cta_primary: 'Inizia gratis',
    cta_secondary: 'Come funziona',
  },
  how: {
    eyebrow: 'COME FUNZIONA',
    headline_l1: 'Cenare con allergie,',
    headline_emphasis: 'semplificato',
    sub: 'Tre passaggi per i clienti. Tre per i ristoranti. Tutti mangiano con fiducia.',
    track_diner: 'Per i clienti',
    track_restaurant: 'Per i ristoranti',
    d1_title: 'Raccontaci della tua tavola',
    d1_body: 'Aggiungi allergie, preferenze e chi ceni con te. Il profilo viaggia con te — nessuna ripetizione.',
    d2_title: 'Chatta con qualsiasi menu',
    d2_body: 'Chiedi "cosa è sicuro per l\'allergia alle arachidi di mia figlia?" e ricevi scelte in secondi. Basato sui veri ingredienti.',
    d3_title: 'Ordina con fiducia',
    d3_body: 'Salva ciò che ha funzionato, condividi con la tavola e riordina senza ricominciare.',
    r1_title: 'Carica il tuo menu',
    r1_body: "Incolla un menu o carica documenti. La nostra IA etichetta allergeni e attributi automaticamente — tu rivedi e pubblichi.",
    r2_title: 'Il tuo staff sembra esperto',
    r2_body: 'I camerieri consultano allergie su qualsiasi piatto in secondi. Zero formazione.',
    r3_title: 'I clienti ti trovano',
    r3_body: 'Clienti attenti alle allergie usano Chat With Menu per trovare ristoranti che prendono sul serio le loro esigenze.',
    stat3_label: 'ristoranti sulla piattaforma',
    cta_title: 'Pronto a mangiare con fiducia?',
    cta_primary: 'Inizia gratis',
    cta_secondary: 'Sfoglia ristoranti',
  },
  dashboard: {
    welcome: 'Benvenuto, {name}',
    welcome_fallback: 'Benvenuto…',
    subtitle_diner:    'Naviga tra scoperta, famiglia e strumenti tavolo senza perderti.',
    subtitle_merchant: 'Gestisci ristoranti, menu e segnali dei clienti.',
    subtitle_server:   'Consultazioni rapide di allergie e speciali mentre servi.',
    role_diner:   'Cliente',
    role_owner:   'Proprietario',
    role_server:  'Cameriere',
    role_admin:   'Admin',
    tab_allergies:   'Allergie',
    tab_preferences: 'Preferenze',
    tab_profile:     'Modifica profilo',
    tab_family:      'Famiglia',
    tab_table:       'Il mio tavolo',
    tab_rewards:     'Ricompense',
    tab_discover:    'Scopri',
    tab_operations:  'Operazioni',
    tab_menus:       'Gestisci menu',
    tab_add_rest:    'Aggiungi ristorante',
    tab_service:     'Strumenti',
  },
  landing: {
    eyebrow: 'Scoperta con IA',
    headline_l1: 'Mai più stress',
    headline_l2: 'su',
    headline_emphasis: 'cosa mangiare',
    sub: "Chatta con il menu di qualsiasi ristorante. Ricevi consigli su misura per le tue allergie, dieta e umore. Impareremo cosa funziona per te.",
    pick_eyebrow: 'Scegli cosa conta per te',
    cta_primary: 'Inizia a scoprire',
    cta_secondary: 'Guarda la demo',
    stat_restaurants: 'Ristoranti',
  },
};

// German (subset)
const de = {
  common: {
    save: 'Speichern', cancel: 'Abbrechen', close: 'Schließen', back: 'Zurück', continue: 'Weiter',
    yes: 'Ja', no: 'Nein', loading: 'Lädt…', saving: 'Speichern…', saved: 'Gespeichert.',
    edit: 'Bearbeiten', delete: 'Löschen', add: 'Hinzufügen', remove: 'Entfernen', done: 'Fertig',
    search: 'Suchen', filter: 'Filtern', clear: 'Löschen', apply: 'Anwenden',
    next: 'Weiter', previous: 'Zurück', skip: 'Überspringen', submit: 'Senden',
    confirm: 'Bestätigen', required: 'Erforderlich', optional: 'Optional',
    error: 'Etwas ist schiefgelaufen.', retry: 'Erneut versuchen',
  },
  nav: {
    discover: 'Entdecken',
    for_restaurants: 'Für Unternehmen',
    pricing: 'Preise',
    dashboard: 'Dashboard',
    notifications: 'Benachrichtigungen',
    sign_in: 'Anmelden',
    sign_out: 'Abmelden',
    get_started: 'Loslegen',
    home_aria: 'Start',
  },
  footer: {
    how_it_works: 'So funktioniert es',
    about: 'Über uns',
    billing: 'Abrechnung',
    settings: 'Einstellungen',
    terms: 'Bedingungen',
    privacy: 'Datenschutz',
    contact: 'Kontakt',
    copyright: '© {year} Chat With Menu',
  },
  chat: {
    empty_title: 'Mit der Karte chatten',
    empty_subtitle: 'Frag nach Allergenen, Kombinationen oder was die Küche anpassen kann.',
    input_placeholder: 'Frag ein Allergen, ein Gericht, die Stimmung…',
    send: 'Senden',
    send_failed: 'Senden fehlgeschlagen. Erneut versuchen.',
    ai_offline: 'Der Assistent holt Luft. Gleich erneut versuchen.',
    reset_chat: 'Neue Unterhaltung',
    suggested_prompts_label: 'Probier',
    suggest_allergens: 'Was ist auf dieser Karte glutenfrei und milchfrei?',
    suggest_recommend: 'Worauf ist die Küche heute Abend am stolzesten?',
    suggest_pair: 'Was passt gut zum Lachs?',
    suggest_cross: 'Habt ihr einen erdnussfreien Bereich?',
  },
  discover: {
    hero_title: 'Restaurants, die dein Körper lieben wird',
    hero_subtitle: 'Swipe nach rechts zum Speichern, links zum Überspringen.',
    brand_tagline: 'Restaurants entdecken',
    auto_banner_title: 'Nach deinem Profil gefiltert',
    auto_banner_body: ' — Orte, die zu {tags} passen.',
    clear_auto: 'Alles zeigen',
  },
  settings: {
    eyebrow: 'EINSTELLUNGEN',
    title_l1: 'Regele, wie wir',
    title_l2: 'dich erreichen',
    language: 'Sprache',
    currency: 'Währung',
  },
  safety: {
    title_l1: 'Wissen, was',
    title_l2: 'wirklich sicher ist',
    share_cta: 'Ein Signal teilen',
    type_worked: 'Hat funktioniert',
    type_wrong: 'Ist schiefgelaufen',
    sev_mild: 'Leicht',
    sev_moderate: 'Mäßig',
    sev_severe: 'Schwer',
    sev_life: 'Lebensbedrohlich',
  },
  menu_manager: {
    title_l1: 'Deine Karte,',
    title_l2: 'sauber und aktuell',
    tab_items: 'Gerichte',
    tab_specials: 'Specials',
    tab_upsell: 'Tipps',
    tab_profile: 'Profil',
    publish: 'Karte veröffentlichen',
    status_live: 'Live',
    status_not_published: 'Nicht veröffentlicht',
  },
  dish: {
    back: 'Zurück',
    menu_fallback: 'KARTE',
    needs_review: 'Überprüfen',
    ask_about_this: 'Dazu fragen',
    next_step_eyebrow: 'NÄCHSTER SCHRITT',
    next_step_title: 'Unsicher, ob dieses Gericht sicher ist?',
    next_step_body: 'Chatte mit der Karte von {name} — wir prüfen deine Allergien mit einem Tipp.',
    share_safety_cta: 'Signal teilen',
    chat_about: 'Chat über {name}',
    allergen_eyebrow: 'ALLERGEN-DETAILS',
    allergen_clear_title: 'Keine Allergene markiert',
    allergen_clear_body: 'Bei schweren Allergien immer beim Personal bestätigen.',
    allergen_count: '{count} Allergene zu beachten',
    allergen_count_one: '1 Allergen zu beachten',
    allergen_hint: 'Schweregrad abgeleitet aus Etiketten, Zubereitungsmethoden und entfernbaren Zutaten.',
    sev_contains: 'Enthält',
    sev_prepared: 'Zubereitet mit',
    sev_may_contain: 'Kann enthalten',
    sev_critical: 'Kritisch',
    prep_eyebrow: 'ZUBEREITUNG',
    prep_title: 'Wie es gemacht wird',
    prep_empty: 'Noch keine Details. Frag in der Küche — die wissen es.',
    prep_methods_eyebrow: 'METHODEN',
    prep_removable_eyebrow: 'KANN WEGGELASSEN WERDEN',
    prep_mods_eyebrow: 'ANPASSUNGEN',
    prep_required: 'erforderlich',
    pair_eyebrow: 'PASST ZU',
    pair_title: 'Vollständige Mahlzeit zusammenstellen',
    pair_empty: 'Wir schlagen Pairings vor, wenn die Karte mehr Gerichte hat.',
    pair_drink: 'Getränk',
    pair_start: 'Vorspeise',
    pair_finish: 'Dessert',
    pair_side: 'Beilage',
    similar_eyebrow_prefix: 'MEHR AUS',
    similar_menu_fallback: 'DIESER KARTE',
    similar_title: 'Ähnliche Gerichte',
    similar_no_allergens: 'Keine Allergene markiert',
    similar_n_allergens: '{n} Allergene',
    similar_one_allergen: '1 Allergen',
    loading: 'Gericht wird geladen…',
    not_found_title: 'Gericht nicht gefunden',
    not_found_body: 'Der Artikel wurde möglicherweise entfernt, oder der Link ist falsch.',
    not_found_cta: 'Zurück',
  },
  restaurant: {
    write_review: 'Bewertung schreiben',
    chat_cta: 'Mit der Karte chatten',
    safety_signal_cta: 'Signal teilen',
    ask_table_cta: 'Den Tisch fragen',
    dietary_eyebrow: 'WAS SIE ANBIETEN',
    menu_highlights: 'MENÜ-HIGHLIGHTS',
    contact_eyebrow: 'KONTAKT',
    contact_none: 'Keine Kontaktinfos hinterlegt.',
    safety_eyebrow: 'SICHERHEITSSIGNALE',
    safety_body: 'Von der Community bestätigte sichere Gerichte. Teile deins, um dem Tisch zu helfen.',
    view_signals: 'Signale ansehen',
    see_full_menu: 'Ganze Karte im Chat',
    no_menu: 'Noch keine Karte veröffentlicht. Chatte für aktuelle Optionen.',
    reviews_title: 'Was Gäste sagen',
    reviews_eyebrow: 'NEUESTE BEWERTUNGEN',
    reviews_count: '{count} Bewertungen',
    reviews_safety_eyebrow: 'ALLERGIE-SICHERHEIT',
    reviews_safety_hint: 'Wie sicher sich Gäste beim Fragen zu Allergenen gefühlt haben.',
    reviews_see_all: 'Alle {count} Bewertungen',
    loading: 'Restaurant wird geladen…',
    not_found_title: 'Restaurant nicht gefunden',
    not_found_body: 'Möglicherweise entfernt, oder der Link ist falsch.',
    not_found_cta: 'Restaurants durchsuchen',
    time_today: 'heute',
    time_yesterday: 'gestern',
    time_days_ago: 'vor {n} Tagen',
    time_week_ago: 'vor 1 Woche',
    time_weeks_ago: 'vor {n} Wochen',
    time_month_ago: 'vor 1 Monat',
    time_months_ago: 'vor {n} Monaten',
  },
  about: {
    eyebrow: 'ÜBER UNS',
    headline_l1: 'Auswärts essen sollte nicht',
    headline_emphasis: 'mit einem Disclaimer kommen',
    sub: 'Chat With Menu existiert für alle, die sich je für eine Allergie entschuldigt, am Tisch eine Zutatenliste zusammengekniffen oder einen langweiligen Salat gegessen haben, weil "sicher" die einzige Option war.',
    story_eyebrow: 'DIE GESCHICHTE',
    story_title: 'Alles begann mit einem schlechten Abendessen.',
    story_p1: 'Eine vierköpfige Familie. Ein neues Restaurant. Ein Kind mit Erdnussallergie. Ein unsicherer Kellner. Ein Manager, der geraten hat. Ein Krankenhausbesuch, der (glücklicherweise) gut endete, aber nicht hätte passieren müssen.',
    story_p2: 'Das Problem war nicht, dass es dem Restaurant egal war — die Information, die alle brauchten (Allergien, Zutaten, Handhabung), lebte nicht am selben Ort. Wir haben Chat With Menu gebaut, damit sie es tut.',
    values_eyebrow: 'WOFÜR WIR BAUEN',
    values_title: 'Unsere Prinzipien',
    v1_title: 'Sicherheit zuerst, immer',
    v1_body: 'Eine falsche Antwort bei einer Allergie ist nie akzeptabel. Wir bauen für diesen Einen-in-Tausend-Fall.',
    v2_title: 'Vertrauen wird verdient',
    v2_body: 'Jedes Allergenlabel, jede Empfehlung, jeder Vertrauenswert — gestützt auf echten Restaurant-Daten.',
    v3_title: 'Community-Signale',
    v3_body: 'Gäste teilen, was funktioniert hat, Restaurants, was sie anbieten. Der Tisch wird mit jedem Besuch klüger.',
    v4_title: 'Essen sollte sich gut anfühlen',
    v4_body: 'Wer eine Einschränkung hat, kennt das flaue Gefühl im Magen in einem neuen Restaurant. Wir machen daraus Freude.',
    timeline_eyebrow: 'ZEITLEISTE',
    timeline_title: 'Wo wir waren',
    ms1_label: 'Idee',
    ms1_body:  'Gegründet nach einem schlechten Abend mit einem erdnussallergischen Kind in einer Stadt voller unetikettierter Karten.',
    ms2_label: 'Start',
    ms2_body:  'Start von Chat With Menu: Frag jede Speisekarte nach deinen Allergien und deiner Ernährung.',
    ms3_label: 'Skalierung',
    ms3_body:  '{count} Restaurants auf Chat With Menu – Tendenz steigend.',
    cta_headline_l1: 'Iss mit',
    cta_headline_emphasis: 'Zuversicht',
    cta_body: 'Schließ dich den Gästen an, die Chat With Menu nutzen, um Restaurants zu finden, die ihren Tisch ernst nehmen.',
    cta_primary: 'Kostenlos starten',
    cta_secondary: 'So funktioniert es',
  },
  how: {
    eyebrow: 'SO FUNKTIONIERT ES',
    headline_l1: 'Essen mit Allergien,',
    headline_emphasis: 'vereinfacht',
    sub: 'Drei Schritte für Gäste. Drei für Restaurants. Alle essen mit Zuversicht.',
    track_diner: 'Für Gäste',
    track_restaurant: 'Für Restaurants',
    d1_title: 'Erzähl uns von deinem Tisch',
    d1_body: 'Füge Allergien, Vorlieben und Begleitung hinzu. Dein Profil reist mit dir — kein Wiederholen in jedem Restaurant.',
    d2_title: 'Chatte mit jeder Karte',
    d2_body: 'Frag "Was ist sicher für die Erdnussallergie meiner Tochter?" und erhalte in Sekunden Empfehlungen. Basierend auf echten Zutaten.',
    d3_title: 'Bestell mit Zuversicht',
    d3_body: 'Speichere, was funktioniert hat, teile sichere Wahlen mit dem Tisch und bestelle wieder ohne Neuanfang.',
    r1_title: 'Gib deine Karte ein',
    r1_body: 'Einfügen oder Dokumente hochladen. Unsere KI tagged Allergene und Attribute automatisch — du prüfst und veröffentlichst.',
    r2_title: 'Dein Team wirkt souverän',
    r2_body: 'Kellner schlagen Allergien in Sekunden nach. Keine Schulung. Jede Gastfrage korrekt beantwortet.',
    r3_title: 'Gäste finden dich',
    r3_body: 'Allergie-bewusste Gäste nutzen Chat With Menu. Mundpropaganda verbreitet sich schnell.',
    stat3_label: 'Restaurants auf der Plattform',
    cta_title: 'Bereit, mit Zuversicht zu essen?',
    cta_primary: 'Kostenlos starten',
    cta_secondary: 'Restaurants durchsuchen',
  },
  dashboard: {
    welcome: 'Willkommen, {name}',
    welcome_fallback: 'Willkommen…',
    subtitle_diner:    'Wechsle zwischen Entdecken, Familie und Tisch-Tools, ohne den Faden zu verlieren.',
    subtitle_merchant: 'Verwalte Restaurants, Karten und Gast-Signale.',
    subtitle_server:   'Schnelle Allergen- und Special-Abfragen beim Servieren.',
    role_diner:   'Gast',
    role_owner:   'Besitzer',
    role_server:  'Kellner',
    role_admin:   'Admin',
    tab_allergies:   'Allergien',
    tab_preferences: 'Vorlieben',
    tab_profile:     'Profil bearbeiten',
    tab_family:      'Familie',
    tab_table:       'Mein Tisch',
    tab_rewards:     'Rewards',
    tab_discover:    'Entdecken',
    tab_operations:  'Operationen',
    tab_menus:       'Karten verwalten',
    tab_add_rest:    'Restaurant hinzufügen',
    tab_service:     'Service-Tools',
  },
  landing: {
    eyebrow: 'KI-gestützte Entdeckung',
    headline_l1: 'Kein Stress mehr',
    headline_l2: 'mit',
    headline_emphasis: 'der Essenswahl',
    sub: 'Chatte mit der Karte jedes Restaurants. Erhalte Empfehlungen, die zu deinen Allergien, deiner Ernährung und deiner Stimmung passen. Wir lernen, was dir guttut.',
    pick_eyebrow: 'Wähle, was dir wichtig ist',
    cta_primary: 'Jetzt entdecken',
    cta_secondary: 'Demo ansehen',
    stat_restaurants: 'Restaurants',
  },
};

// Japanese (subset)
const ja = {
  common: {
    save: '保存', cancel: 'キャンセル', close: '閉じる', back: '戻る', continue: '続ける',
    yes: 'はい', no: 'いいえ', loading: '読み込み中…', saving: '保存中…', saved: '保存しました。',
    edit: '編集', delete: '削除', add: '追加', remove: '削除', done: '完了',
    search: '検索', filter: 'フィルター', clear: 'クリア', apply: '適用',
    next: '次へ', previous: '戻る', skip: 'スキップ', submit: '送信',
    confirm: '確認', required: '必須', optional: '任意',
    error: '問題が発生しました。', retry: '再試行',
  },
  nav: {
    discover: '探す',
    for_restaurants: '店舗向け',
    pricing: '料金',
    dashboard: 'ダッシュボード',
    notifications: '通知',
    sign_in: 'ログイン',
    sign_out: 'ログアウト',
    get_started: '始める',
    home_aria: 'ホーム',
  },
  footer: {
    how_it_works: '使い方',
    about: '概要',
    billing: '請求',
    settings: '設定',
    terms: '利用規約',
    privacy: 'プライバシー',
    contact: 'お問い合わせ',
    copyright: '© {year} Chat With Menu',
  },
  chat: {
    empty_title: 'メニューとチャット',
    empty_subtitle: 'アレルゲン、ペアリング、キッチンがカスタマイズできることを聞いてください。',
    input_placeholder: 'アレルゲン、料理、雰囲気について聞いてください…',
    send: '送信',
    send_failed: '送信失敗。再試行してください。',
    ai_offline: 'アシスタントが一息ついています。しばらくしてから再試行してください。',
    reset_chat: '新しい会話',
    suggested_prompts_label: '試しに聞いてみる',
    suggest_allergens: 'このメニューでグルテンフリーで乳製品なしのものは?',
    suggest_recommend: '今夜、キッチンが最も誇りに思っているのは?',
    suggest_pair: 'サーモンに合うのは?',
    suggest_cross: 'ピーナッツフリーのステーションはありますか?',
  },
  discover: {
    hero_title: '体が喜ぶレストラン',
    hero_subtitle: '右にスワイプで保存、左でスキップ。',
    brand_tagline: 'レストランを探す',
    auto_banner_title: 'プロフィールからフィルター',
    auto_banner_body: ' — {tags} に合う場所を表示。',
    clear_auto: 'すべて表示',
  },
  settings: {
    eyebrow: '設定',
    language: '言語',
    currency: '通貨',
  },
  safety: {
    type_worked: 'うまくいった',
    type_wrong: '問題があった',
    sev_mild: '軽度',
    sev_moderate: '中程度',
    sev_severe: '重度',
    sev_life: '命に関わる',
  },
  menu_manager: {
    tab_items: 'メニュー',
    tab_specials: '特別',
    tab_upsell: 'ヒント',
    tab_profile: 'プロフィール',
    publish: 'メニューを公開',
    status_live: '公開中',
    status_not_published: '未公開',
  },
  dish: {
    back: '戻る',
    menu_fallback: 'メニュー',
    needs_review: '要確認',
    ask_about_this: 'これについて聞く',
    next_step_eyebrow: '次のステップ',
    next_step_title: 'この料理が安全か不安ですか?',
    next_step_body: '{name}のメニューとチャット — タップ一つでアレルギーをクロスチェック。',
    share_safety_cta: 'シグナルを共有',
    chat_about: '{name}についてチャット',
    allergen_eyebrow: 'アレルゲンの詳細',
    allergen_clear_title: 'アレルゲン表示なし',
    allergen_clear_body: '重度のアレルギーがある場合は必ず店員に確認してください。',
    allergen_count: '注意すべきアレルゲン{count}件',
    allergen_count_one: '注意すべきアレルゲン1件',
    allergen_hint: 'タグ、調理方法、除去可能な成分から推定。',
    sev_contains: '含む',
    sev_prepared: '調理に使用',
    sev_may_contain: '含む可能性',
    sev_critical: '危険',
    prep_eyebrow: '調理',
    prep_title: 'どう作るか',
    prep_empty: '詳細なし。キッチンに聞いてください。',
    prep_methods_eyebrow: '調理法',
    prep_removable_eyebrow: '除去可能',
    prep_mods_eyebrow: 'カスタマイズ',
    prep_required: '必須',
    pair_eyebrow: '相性が良い',
    pair_title: '完全な食事を組み立てる',
    pair_empty: 'メニューが増えたらペアリングを提案します。',
    pair_drink: '飲み物',
    pair_start: '前菜',
    pair_finish: 'デザート',
    pair_side: 'サイド',
    similar_eyebrow_prefix: 'もっと見る',
    similar_menu_fallback: 'このメニュー',
    similar_title: '似た料理',
    similar_no_allergens: 'アレルゲン表示なし',
    similar_n_allergens: '{n}件のアレルゲン',
    similar_one_allergen: '1件のアレルゲン',
    loading: '料理を読み込み中…',
    not_found_title: '料理が見つかりません',
    not_found_body: 'メニューから削除されたか、リンクが正しくない可能性があります。',
    not_found_cta: '戻る',
  },
  restaurant: {
    write_review: 'レビューを書く',
    chat_cta: 'メニューとチャット',
    safety_signal_cta: 'シグナルを共有',
    ask_table_cta: 'テーブルに聞く',
    dietary_eyebrow: '対応しているもの',
    menu_highlights: 'メニューのハイライト',
    contact_eyebrow: '連絡先',
    contact_none: '連絡先情報は公開されていません。',
    safety_eyebrow: '安全シグナル',
    safety_body: 'コミュニティ検証済みの安全な料理。あなたの経験を共有してください。',
    view_signals: 'シグナルを見る',
    see_full_menu: 'チャットで全メニューを見る',
    no_menu: 'メニューはまだ公開されていません。最新の選択肢はチャットで。',
    reviews_title: '利用者の声',
    reviews_eyebrow: '最近のレビュー',
    reviews_count: '{count}件のレビュー',
    reviews_safety_eyebrow: 'アレルギーの安全性',
    reviews_safety_hint: 'アレルゲンについて質問する際にどれだけ安心できたか。',
    reviews_see_all: '{count}件のレビューをすべて見る',
    loading: 'レストランを読み込み中…',
    not_found_title: 'レストランが見つかりません',
    not_found_body: '削除されたか、リンクが正しくない可能性があります。',
    not_found_cta: 'レストランを見る',
    time_today: '今日',
    time_yesterday: '昨日',
    time_days_ago: '{n}日前',
    time_week_ago: '1週間前',
    time_weeks_ago: '{n}週間前',
    time_month_ago: '1か月前',
    time_months_ago: '{n}か月前',
  },
  about: {
    eyebrow: '私たちについて',
    headline_l1: '外食に',
    headline_emphasis: '注意書きはいらない',
    sub: 'Chat With Menuは、アレルギーを謝った経験、メニューで成分表を目を細めて読んだ経験、「安全」だけの理由で退屈なサラダを食べた経験のあるすべての人のために存在します。',
    story_eyebrow: 'ストーリー',
    story_title: '悪い夕食から始まりました。',
    story_p1: '4人家族、新しいレストラン、ピーナッツアレルギーの子ども、確信のない店員、推測したマネージャー、幸いにも無事に終わった病院行き。しかし起こるべきではありませんでした。',
    story_p2: '問題はレストランが気にかけていなかったのではなく、必要な情報（客のアレルギー、料理の成分、厨房の扱い）が同じ場所に存在しなかったことです。Chat With Menuはそれを可能にします。',
    values_eyebrow: '私たちが作る理由',
    values_title: '私たちの原則',
    v1_title: '常に安全第一',
    v1_body: 'アレルギーについての誤った答えは決して許容できません。その1,000人に1人のケースのために作ります。',
    v2_title: '信頼は勝ち取るもの',
    v2_body: 'すべてのアレルゲン表示、推奨、信頼度は、レストランの実データに裏打ちされています。',
    v3_title: 'コミュニティのシグナル',
    v3_body: '客は何が効いたかを共有し、レストランは何に対応できるかを共有します。テーブルは食事のたびに賢くなります。',
    v4_title: '食事は気持ち良いもの',
    v4_body: '制限のある人なら、新しいレストランでのあの不安を知っています。それを喜びに変えます。',
    timeline_eyebrow: 'タイムライン',
    timeline_title: 'これまでの歩み',
    ms1_label: 'アイデア',
    ms1_body:  'ラベルのないメニューだらけの街で、ピーナッツアレルギーの子どもとの悪い夜の後に設立。',
    ms2_label: 'ローンチ',
    ms2_body:  'Chat With Menuをローンチ。どのメニューにもアレルギーや食事制限について質問できるように。',
    ms3_label: '拡大',
    ms3_body:  'Chat With Menuに{count}店舗が掲載中。',
    cta_headline_l1: '自信を持って',
    cta_headline_emphasis: '食事を',
    cta_body: 'Chat With Menuで、テーブルを真剣に考えるレストランを見つけましょう。',
    cta_primary: '無料で始める',
    cta_secondary: '使い方を見る',
  },
  how: {
    eyebrow: '使い方',
    headline_l1: 'アレルギーとの外食を',
    headline_emphasis: 'シンプルに',
    sub: '利用者のための3ステップ。レストランのための3ステップ。誰もが自信を持って食事を。',
    track_diner: '利用者向け',
    track_restaurant: '店舗向け',
    d1_title: 'テーブルを教えてください',
    d1_body: 'アレルギー、好み、一緒に食事する人を追加。プロフィールは一緒に移動—毎回繰り返す必要なし。',
    d2_title: 'どんなメニューともチャット',
    d2_body: '「娘のピーナッツアレルギーに安全なものは?」と聞けば、数秒で候補が出ます。実際の成分に基づく—推測ではなく。',
    d3_title: '自信を持って注文',
    d3_body: '良かったものを保存、安全な選択をテーブルと共有、次回もゼロから始めず再注文。',
    r1_title: 'メニューをアップ',
    r1_body: 'メニューを貼り付けるか書類をアップ。AIが自動でアレルゲンと属性をタグ付け—確認して公開。',
    r2_title: 'スタッフが頼もしく見える',
    r2_body: 'スタッフがどんな料理でも数秒でアレルギー確認。トレーニング不要。すべての質問に正しく答えます。',
    r3_title: '利用者があなたを見つける',
    r3_body: 'アレルギーに気を配る利用者はChat With Menuを使い、真剣な店を見つけます。口コミが広がります。',
    stat3_label: 'プラットフォーム上の店舗',
    cta_title: '自信を持って食べる準備は?',
    cta_primary: '無料で始める',
    cta_secondary: '店舗を見る',
  },
  dashboard: {
    welcome: 'ようこそ、{name}さん',
    welcome_fallback: 'ようこそ…',
    subtitle_diner:    '発見、家族、テーブルツールを自由に行き来できます。',
    subtitle_merchant: 'レストラン、メニュー、ゲストシグナルを管理。',
    subtitle_server:   '接客中にアレルギーや特別メニューをすばやく確認。',
    role_diner:   'ゲスト',
    role_owner:   'オーナー',
    role_server:  'スタッフ',
    role_admin:   '管理者',
    tab_allergies:   'アレルギー',
    tab_preferences: '好み',
    tab_profile:     'プロフィール編集',
    tab_family:      '家族',
    tab_table:       'マイテーブル',
    tab_rewards:     'リワード',
    tab_discover:    '探す',
    tab_operations:  'オペレーション',
    tab_menus:       'メニュー管理',
    tab_add_rest:    '店舗を追加',
    tab_service:     'サービスツール',
  },
  landing: {
    eyebrow: 'AIによる発見',
    headline_l1: 'もう悩まない',
    headline_l2: '',
    headline_emphasis: '何を食べるか',
    sub: 'どんなレストランのメニューともチャット。アレルギー、食事制限、その日の気分に合わせた提案を受け取れます。あなたに合うものを学んでいきます。',
    pick_eyebrow: '大切なことを選んでください',
    cta_primary: '探し始める',
    cta_secondary: 'デモを見る',
    stat_restaurants: 'レストラン',
  },
};

// Chinese (simplified, subset)
const zh = {
  common: {
    save: '保存', cancel: '取消', close: '关闭', back: '返回', continue: '继续',
    yes: '是', no: '否', loading: '加载中…', saving: '保存中…', saved: '已保存。',
    edit: '编辑', delete: '删除', add: '添加', remove: '移除', done: '完成',
    search: '搜索', filter: '筛选', clear: '清除', apply: '应用',
    next: '下一个', previous: '上一个', skip: '跳过', submit: '提交',
    confirm: '确认', required: '必填', optional: '可选',
    error: '出了点问题。', retry: '重试',
  },
  nav: {
    discover: '发现',
    for_restaurants: '商家入驻',
    pricing: '价格',
    dashboard: '仪表板',
    notifications: '通知',
    sign_in: '登录',
    sign_out: '退出',
    get_started: '开始',
    home_aria: '主页',
  },
  footer: {
    how_it_works: '使用方法',
    about: '关于',
    billing: '账单',
    settings: '设置',
    terms: '条款',
    privacy: '隐私',
    contact: '联系',
    copyright: '© {year} Chat With Menu',
  },
  chat: {
    empty_title: '和菜单聊天',
    empty_subtitle: '问问过敏原、搭配,或厨房可以定制什么。',
    input_placeholder: '问一个过敏原、一道菜、氛围…',
    send: '发送',
    send_failed: '发送失败。重试。',
    ai_offline: '助手在喘口气。稍后重试。',
    reset_chat: '新对话',
    suggested_prompts_label: '试试问',
    suggest_allergens: '这份菜单上什么是无麸质无乳制品的?',
    suggest_recommend: '今晚厨房最自豪的是什么?',
    suggest_pair: '什么和三文鱼搭配?',
    suggest_cross: '你们有无花生的工作站吗?',
  },
  discover: {
    hero_title: '让身体满意的餐厅',
    hero_subtitle: '右划保存,左划跳过。',
    brand_tagline: '发现餐厅',
    auto_banner_title: '按您的档案筛选',
    auto_banner_body: ' — 显示匹配 {tags} 的位置。',
    clear_auto: '显示全部',
  },
  settings: {
    eyebrow: '设置',
    language: '语言',
    currency: '货币',
  },
  safety: {
    type_worked: '成功',
    type_wrong: '出问题了',
    sev_mild: '轻度',
    sev_moderate: '中度',
    sev_severe: '严重',
    sev_life: '危及生命',
  },
  menu_manager: {
    tab_items: '菜品',
    tab_specials: '特色',
    tab_upsell: '提示',
    tab_profile: '资料',
    publish: '发布菜单',
    status_live: '已上线',
    status_not_published: '未发布',
  },
  dish: {
    back: '返回',
    menu_fallback: '菜单',
    needs_review: '需复核',
    ask_about_this: '询问此菜',
    next_step_eyebrow: '下一步',
    next_step_title: '不确定这道菜是否安全?',
    next_step_body: '与 {name} 的菜单对话 — 一键交叉核对您的过敏。',
    share_safety_cta: '分享信号',
    chat_about: '聊聊 {name}',
    allergen_eyebrow: '过敏原详情',
    allergen_clear_title: '无过敏原标记',
    allergen_clear_body: '如有严重过敏，请始终与服务员确认。',
    allergen_count: '需注意 {count} 种过敏原',
    allergen_count_one: '需注意 1 种过敏原',
    allergen_hint: '根据标签、烹饪方法和可移除成分推断严重程度。',
    sev_contains: '含有',
    sev_prepared: '使用制作',
    sev_may_contain: '可能含有',
    sev_critical: '关键',
    prep_eyebrow: '烹饪',
    prep_title: '制作方式',
    prep_empty: '暂无详情。可询问厨房——他们清楚。',
    prep_methods_eyebrow: '方法',
    prep_removable_eyebrow: '可以移除',
    prep_mods_eyebrow: '自定义',
    prep_required: '必选',
    pair_eyebrow: '搭配',
    pair_title: '搭配一顿完整餐点',
    pair_empty: '菜单菜品更多时我们会推荐搭配。',
    pair_drink: '饮品搭配',
    pair_start: '前菜',
    pair_finish: '甜点',
    pair_side: '配菜',
    similar_eyebrow_prefix: '更多来自',
    similar_menu_fallback: '此菜单',
    similar_title: '相似菜品',
    similar_no_allergens: '无过敏原标记',
    similar_n_allergens: '{n} 种过敏原',
    similar_one_allergen: '1 种过敏原',
    loading: '加载菜品中…',
    not_found_title: '未找到菜品',
    not_found_body: '该项目可能已从菜单移除，或链接不正确。',
    not_found_cta: '返回',
  },
  restaurant: {
    write_review: '写评价',
    chat_cta: '与菜单对话',
    safety_signal_cta: '分享安全信号',
    ask_table_cta: '询问餐桌',
    dietary_eyebrow: '支持什么',
    menu_highlights: '菜单亮点',
    contact_eyebrow: '联系方式',
    contact_none: '未发布联系信息。',
    safety_eyebrow: '安全信号',
    safety_body: '社区验证的安全菜品。分享您的经历。',
    view_signals: '查看信号',
    see_full_menu: '在聊天中查看完整菜单',
    no_menu: '尚未发布菜单。可通过聊天获取最新选项。',
    reviews_title: '顾客怎么说',
    reviews_eyebrow: '最新评价',
    reviews_count: '{count} 条评价',
    reviews_safety_eyebrow: '过敏安全',
    reviews_safety_hint: '顾客询问过敏原时的信心度。',
    reviews_see_all: '查看全部 {count} 条评价',
    loading: '加载餐厅中…',
    not_found_title: '未找到餐厅',
    not_found_body: '可能已被移除，或链接不正确。',
    not_found_cta: '浏览餐厅',
    time_today: '今天',
    time_yesterday: '昨天',
    time_days_ago: '{n} 天前',
    time_week_ago: '1 周前',
    time_weeks_ago: '{n} 周前',
    time_month_ago: '1 个月前',
    time_months_ago: '{n} 个月前',
  },
  about: {
    eyebrow: '关于我们',
    headline_l1: '在外用餐',
    headline_emphasis: '不该需要免责声明',
    sub: 'Chat With Menu 为每一位曾因过敏道歉、在餐桌上眯眼看配料表、或因"安全"只剩无聊沙拉的人而存在。',
    story_eyebrow: '故事',
    story_title: '我们从一次糟糕的晚餐开始。',
    story_p1: '一家四口。一家新餐厅。一个花生过敏的孩子。一位拿不准的服务员。一位猜测的经理。一次（幸好）安然结束但本不该发生的住院。',
    story_p2: '问题不是餐厅不在乎——而是各方需要的信息（顾客的过敏、菜品的成分、厨房的处理）没有住在同一个地方。我们打造 Chat With Menu，让它们住在一起。',
    values_eyebrow: '我们为何打造',
    values_title: '我们的原则',
    v1_title: '始终安全第一',
    v1_body: '对过敏的错误回答永远不可接受。我们为那千分之一的情况打造，因为对那位顾客来说，那就是唯一的情况。',
    v2_title: '信任需要赢得',
    v2_body: '每个过敏原标签、每个推荐、每个置信度——都有餐厅的真实数据支撑，而非猜测。',
    v3_title: '社区信号',
    v3_body: '顾客分享什么行得通，餐厅分享他们能适应什么。每次用餐，餐桌都变得更聪明。',
    v4_title: '用餐应当感觉良好',
    v4_body: '有饮食限制的人都懂新餐厅里胃里那种紧张。我们来把它变成喜悦。',
    timeline_eyebrow: '时间线',
    timeline_title: '我们走过的路',
    ms1_label: '构想',
    ms1_body:  '在一个满是未标注菜单的城市，和一个花生过敏的孩子度过糟糕一夜之后成立。',
    ms2_label: '启动',
    ms2_body:  'Chat With Menu 上线：向任何菜单询问你的过敏和饮食需求。',
    ms3_label: '扩展',
    ms3_body:  'Chat With Menu 上已有 {count} 家餐厅，并在持续增加。',
    cta_headline_l1: '自信地',
    cta_headline_emphasis: '享用美食',
    cta_body: '和其他顾客一起使用 Chat With Menu，发现真正认真对待餐桌的餐厅。',
    cta_primary: '免费开始',
    cta_secondary: '了解如何运作',
  },
  how: {
    eyebrow: '工作原理',
    headline_l1: '带着过敏用餐，',
    headline_emphasis: '变得简单',
    sub: '给顾客的三步。给餐厅的三步。每个人都能自信用餐。',
    track_diner: '顾客',
    track_restaurant: '餐厅',
    d1_title: '告诉我们您的餐桌',
    d1_body: '添加过敏、偏好和同餐者。档案随您而行——不必在每家餐厅重复说明。',
    d2_title: '与任何菜单对话',
    d2_body: '问"我女儿的花生过敏什么安全?"几秒内得到排序推荐。基于餐厅真实成分——而非猜测。',
    d3_title: '自信地下单',
    d3_body: '保存有效的选择，与餐桌分享安全选项，下次无需从头开始。',
    r1_title: '上传您的菜单',
    r1_body: '粘贴菜单或上传文件。AI 自动标注过敏原和饮食属性——您审阅并发布。',
    r2_title: '您的员工看起来很专业',
    r2_body: '服务员几秒内查询任何菜品的过敏信息。零培训。每个顾客问题都能正确回答。',
    r3_title: '顾客找到您',
    r3_body: '关注过敏的顾客使用 Chat With Menu 找到认真对待需求的餐厅。口碑传播迅速。',
    stat3_label: '平台上的餐厅',
    cta_title: '准备好自信用餐了吗?',
    cta_primary: '免费开始',
    cta_secondary: '浏览餐厅',
  },
  dashboard: {
    welcome: '欢迎，{name}',
    welcome_fallback: '欢迎…',
    subtitle_diner:    '在发现、家庭和餐桌工具之间流畅切换。',
    subtitle_merchant: '管理您的餐厅、菜单和顾客信号。',
    subtitle_server:   '服务中快速查询过敏信息和特别菜品。',
    role_diner:   '顾客',
    role_owner:   '店主',
    role_server:  '服务员',
    role_admin:   '管理员',
    tab_allergies:   '过敏',
    tab_preferences: '偏好',
    tab_profile:     '编辑资料',
    tab_family:      '家人',
    tab_table:       '我的餐桌',
    tab_rewards:     '奖励',
    tab_discover:    '发现',
    tab_operations:  '运营',
    tab_menus:       '管理菜单',
    tab_add_rest:    '添加餐厅',
    tab_service:     '服务工具',
  },
  landing: {
    eyebrow: 'AI 驱动的发现',
    headline_l1: '不再为',
    headline_l2: '',
    headline_emphasis: '吃什么',
    sub: '与任何餐厅的菜单对话。根据您的过敏情况、饮食和心情获得个性化推荐。我们会了解什么适合您。',
    pick_eyebrow: '选择对您重要的',
    cta_primary: '开始发现',
    cta_secondary: '观看演示',
    stat_restaurants: '餐厅',
  },
};

export const translations = { en, es, fr, pt, it, de, ja, zh };
